/**
 * The menu, as a pure state machine. No DOM, no timers, no globals.
 *
 * Everything the front end does — which screen you are on, which row is
 * focused, what a row currently reads, what pressing Enter on it does — is a
 * function of this object. `src/ui/menu.ts` renders it and feeds it
 * `NavAction`s; it never decides anything itself. That split is the whole
 * reason the navigation can be tested exhaustively (see test/ui/menu-model),
 * including the property that matters most: no input sequence can strand the
 * player on a screen with no way back to the main menu.
 *
 * ===========================================================================
 * NAVIGATION
 * ===========================================================================
 *
 * Screens form a STACK, not a graph of hard-coded back-links. `camera` and
 * `controls` are reachable from the main menu, from match setup and from the
 * pause menu, and a stack is what makes Escape return to whichever of those
 * you actually came from without any of them knowing about each other.
 *
 * Three kinds of transition:
 *
 *   push    main -> mode -> setup, and anything -> camera/controls/mutators.
 *           Escape pops.
 *   reset   starting, restarting, abandoning or finishing a match. The stack is
 *           replaced, because "back" out of a match must not walk you through
 *           the setup screens you started from.
 *   pause   Escape during play PUSHES the pause menu rather than popping.
 *           This is the one deliberate exception to "Escape backs out", and it
 *           is not a dead end: pause has an explicit route to the main menu.
 *
 * `back` at the root (`main`) is a no-op — the root is not a trap, it is the
 * root. There is no "quit" because there is nothing to quit to in a browser.
 *
 * ===========================================================================
 * WHAT IS LIVE AND WHAT IS NOT
 * ===========================================================================
 *
 * Rows whose underlying feature does not exist yet are emitted with
 * `enabled: false` and a `note` saying why, and left/right on them does
 * nothing. They are still focusable, so the note can be read. This is the rule
 * the whole front end is held to: a control that cannot work must not look like
 * it worked. See `src/game/settings.ts` for the per-option flags.
 */

import {
  AUTO_THROTTLE_CHOICES,
  CAR_CHOICES,
  DEFAULT_BALL_CAM,
  LENGTH_CHOICES,
  MODE_CHOICES,
  MUTATOR_ROWS,
  TEAM_CHOICES,
  TOUCH_MODE_CHOICES,
  TRAINING_ROWS,
  defaultAudioSettings,
  difficultyChoicesFor,
  type BotDifficulty,
  type Choice,
  type GameSettings,
  type TeamSide,
} from '../game/settings';
import {
  CAMERA_SETTING_RANGES,
  clampCameraSettings,
  defaultCameraSettings,
  type CameraSettings,
} from '../render/camera';
import { MULTIPLAYER_MODE_CHOICES, isRoomCode } from '../game/settings';
import { DRILLS, drillById, type DrillSummary } from '../game/drills';
import type { MultiplayerMode } from '../net/protocol';
import type { TrainerPhase } from '../rl/types';
import { BINDING_ROWS, bindKey, clearBinding, resetBindings, slotLabels, type BindableAction } from './bindings';
import type { NavAction } from './nav-input';
import {
  TEXT_FIELDS,
  commitText,
  displayTextValue,
  editText,
  everyoneReady,
  idleNetView,
  netStatusLine,
  pasteText,
  type NetView,
  type TextFieldId,
} from './net-model';
import type { AccountTone, AccountView } from './account';
import {
  checkpointRowLabel,
  isTrainingActive,
  isTrainingLive,
  runRowLabel,
  type CheckpointRow,
} from './training-model';
import {
  PASS_PREMIUM_COST,
  PASS_TIERS,
  type CosmeticSlot,
  type MatchStats,
} from '../game/cosmetics';
import { activeLoadout } from '../game/inventory';
import {
  GARAGE_ITEM_PREFIX,
  GARAGE_PRESET_PREFIX,
  GARAGE_SLOTS,
  GARAGE_SLOT_PREFIX,
  SLOT_LABEL,
  colorChoices,
  cycleColor,
  garageRows,
  itemForInstance,
  ownsInstance,
  presetRows,
  slotRows,
} from './garage';
import { passRewardLabel, tierLabel } from './progression-model';
import {
  challengeRowNote,
  challengeRows,
  passRow,
  progressLabel,
  rarityLabel,
  shopRowNote,
  shopRows,
  untilLabel,
  type ChallengeRow,
  type ProgressionState,
  type ShopRow,
} from './progression-model';

/** Prefix for mutator row ids. Defined once so the item id and the handler
 *  that parses it back cannot drift apart. */
const MUTATOR_PREFIX = 'mutator:';

/** Same contract as `MUTATOR_PREFIX`, for the training settings rows. */
const TRAINING_PREFIX = 'training:';

/** Same contract again, for one stored checkpoint's row. */
const CHECKPOINT_PREFIX = 'ckpt:';

/** And again, for the quick-match mode rows. */
const NET_MODE_PREFIX = 'net-mode:';

/** And again, for the two on-screen-control rows on the controls screen. */
const TOUCH_PREFIX = 'touch:';

/** And again, for the practice drill rows. */
const DRILL_PREFIX = 'drill:';

/** And again, for the three rows that open the text prompt. */
const TEXT_PREFIX = 'text:';

/** And again, for the ten shop rows: `shop:featured:2`, `shop:daily:5`. */
const SHOP_PREFIX = 'shop:';

/** And again, for one challenge's row. */
const CHALLENGE_PREFIX = 'chal:';

/**
 * Tiers visible beyond the highest one reached, so the Pro Tiers can be looked
 * at before they are earned. The pass does not end at seventy — see
 * src/game/rocketpass.ts — so the cycler needs a bound that is not a cap.
 */
const PASS_LOOKAHEAD = 10;

// ---------------------------------------------------------------------------
// Screens, items, effects
// ---------------------------------------------------------------------------

export type Screen =
  /**
   * THE FIRST SCREEN. Three ways in — sign in, create an account, or play on
   * your own — because those are genuinely the only three things a person
   * arriving here wants, and deciding between them is not something to do
   * while also looking at Camera, Audio, Controls and a locked shop.
   *
   * Skipped entirely for a player who is already signed in: a restored session
   * moves straight to `main`, so the reward for having an account is never
   * being asked about it again.
   */
  | 'welcome'
  | 'main'
  | 'mode'
  | 'setup'
  | 'mutators'
  | 'camera'
  /** Master volume and mute. */
  | 'audio'
  | 'controls'
  | 'bind'
  | 'text'
  | 'training'
  | 'checkpoints'
  | 'checkpoint'
  /** The ladder: your standing, the leaderboard, and your recent matches. */
  | 'ranked'
  // --- practice ---
  /** The drill catalogue. Nothing else lives here — a drill is picked and run. */
  | 'drills'
  /** A finished run's tally. Reached only from a run, never navigated to. */
  | 'drillresult'
  | 'countdown'
  | 'match'
  | 'pause'
  | 'postmatch'
  // --- garage ---
  /** Team colour, the two paints, every visual slot, and the presets. */
  | 'garage'
  /** One slot's item picker: owned copies, then what the catalogue has. */
  | 'garageSlot'
  /** The preset list. A preset is a PAIR of loadouts, one per team colour. */
  | 'garagePresets'
  // --- progression ---
  /** The daily and featured rotations. Credits are earned; nothing is bought
   *  with money anywhere in this codebase. */
  | 'shop'
  /** The tier track, both rows, and the premium unlock. */
  | 'rocketpass'
  /** Weekly, Season and Event, with their counters. */
  | 'challenges'
  // --- account ---
  /** The account hub: a signed-in profile, or the two ways to get one. */
  | 'account'
  /** Email and password. Reachable from `welcome` and from `account`. */
  | 'signin'
  /** Name, email and password, and the verification mail resend. */
  | 'register'
  // --- multiplayer ---
  /** The entry point: quick match, host, join, and where the server lives. */
  | 'multiplayer'
  /** The quick-match mode picker. */
  | 'netmode'
  /** Connecting, handshaking or queueing. Always cancellable. */
  | 'connecting'
  /** Seated in a room that has not started. */
  | 'lobby'
  /** A networked match, live. The overlay is out of the way. */
  | 'netmatch'
  /** Escape during a networked match. Does NOT pause anything — see the header. */
  | 'netpause'
  /** The link went away, or the server refused. Never a dead end. */
  | 'disconnected';

/**
 * Screens the simulation runs behind; also the ones with no menu rows.
 *
 * `netmatch` is here for the same reason `match` is, but it means something
 * subtly different and the difference is worth stating: a local match STOPS
 * when this says so, and a networked one cannot. The server keeps simulating
 * whatever this menu is showing, so `netpause` suspends the local player's
 * INPUT and nothing else — see `MatchSession.setPaused` in ./menu.ts and the
 * networked branch of `App.tick`.
 */
const LIVE_SCREENS: readonly Screen[] = ['countdown', 'match', 'netmatch'];

/**
 * The way in: the screens whose entire job is getting a session, and therefore
 * the only ones a session arriving is allowed to navigate away from.
 */
const ENTRY_SCREENS: readonly Screen[] = ['welcome', 'signin', 'register'];

/** Screens that report what the account runtime last did. */
const ACCOUNT_SCREENS: readonly Screen[] = ['welcome', 'signin', 'register', 'account'];

/** Screens that belong to a multiplayer session, for routing net events. */
const NET_SCREENS: readonly Screen[] = [
  'multiplayer',
  'netmode',
  'connecting',
  'lobby',
  'netmatch',
  'netpause',
  'disconnected',
];

export interface MenuItemBase {
  id: string;
  label: string;
  enabled: boolean;
  /** Explanation shown while the row is focused — usually why it is locked. */
  note?: string;
  /**
   * Word on the lock chip. Defaults to "Soon", which is right for a feature
   * that does not exist yet and WRONG for one that is merely unavailable right
   * now: a hyperparameter locked because a run is in progress is not a promise
   * about a future version, and a chip that says so is a small lie repeated on
   * six rows.
   */
  lockLabel?: string;
}

export type MenuItem =
  | (MenuItemBase & { kind: 'action'; emphasis?: boolean })
  | (MenuItemBase & { kind: 'choice'; valueLabel: string; index: number; count: number })
  | (MenuItemBase & { kind: 'toggle'; on: boolean })
  | (MenuItemBase & {
      kind: 'slider';
      value: number;
      min: number;
      max: number;
      valueLabel: string;
    })
  | (MenuItemBase & { kind: 'bind'; action: BindableAction; slots: string[]; slot: number })
  /**
   * A read-only value with an action behind it: a shop price, a pass reward, a
   * "3 / 10" challenge counter.
   *
   * Distinct from `choice` precisely because it has no next value. A choice row
   * draws chevrons, and a pair of arrows that do nothing is the same lie as an
   * enabled locked row — the rule the header states. Confirm runs the action;
   * left and right do nothing at all.
   */
  | (MenuItemBase & {
      kind: 'value';
      valueLabel: string;
      /** Small chip before the value: "Owned", "Claimed", "Ready", a rarity. */
      badge?: string;
      /**
       * Colour for that chip, as a CSS string. The garage drives it off
       * `RARITY_HEX` so a Black Market item reads as one at a glance — which is
       * the single most useful thing a rarity can do on a list of forty rows.
       */
      badgeTone?: string;
      /** A colour dot before the label: a painted variant's own colour. */
      swatch?: string;
      /** 0..1 bar under the label, for a challenge counter. */
      fraction?: number;
      emphasis?: boolean;
    })
  /**
   * A free-text setting. Confirm opens the modal prompt; left/right do nothing,
   * because there is no "next" address.
   *
   * Free text is the one input this front end had avoided — every other row is a
   * list, for the reason `TRAINING_ROWS` gives — and three fields genuinely need
   * it: a server address, a join code and a name are all values only the player
   * knows. They are all validated on commit through the same sanitizers storage
   * uses, so the field cannot accept something the rest of the game rejects.
   */
  | (MenuItemBase & { kind: 'text'; field: TextFieldId; value: string; empty: boolean });

/**
 * What the view must DO about an input the model has already accounted for.
 *
 * The training effects follow the same rule as the match ones, and for a
 * sharper reason: the model must stay constructible in Node with no Worker, no
 * IndexedDB and no trainer at all, so it cannot own the run. It records that a
 * run was asked to start; whether one did is reported back through
 * `setTrainingPhase`. That split is also what keeps test/ui/menu-model's
 * exhaustive search from spawning worker threads.
 */
export type MenuEffect =
  /** Settings mutated: persist them, and apply the live ones. */
  | 'settings-changed'
  /** Build a world from `settings.setup` and start ticking. */
  | 'start-match'
  /** Build a practice session for `selectedDrillId` and start ticking. */
  | 'start-drill'
  /** Re-read the ladder, the player's standing and their history. */
  | 'ranked-refresh'
  /** Tear the current world down and build a fresh one from the same setup. */
  | 'restart-match'
  /** Tear the current world down; nothing replaces it. */
  | 'quit-match'
  /** Begin a run, or resume a suspended one, from the current settings. */
  | 'training-start'
  | 'training-pause'
  | 'training-resume'
  /** End the run. Its checkpoint survives only if it was saved. */
  | 'training-stop'
  /** A hyperparameter row moved; the next run picks it up. */
  | 'training-config-changed'
  /** The watch-live toggle moved. */
  | 'training-watch-changed'
  /** Re-read the checkpoint store into `checkpoints`. */
  | 'checkpoints-refresh'
  /** Write the trainer's current state to the store. */
  | 'checkpoint-save'
  /** Open a file picker and import what comes back. */
  | 'checkpoint-import'
  /** Make `selectedCheckpointId` the live policy. */
  | 'checkpoint-load'
  /** Download `selectedCheckpointId` as JSON. */
  | 'checkpoint-export'
  | 'checkpoint-delete'
  // --- multiplayer ---
  /** Open a connection for `netIntent`. The runtime owns the socket. */
  | 'net-connect'
  /** Abandon a connection that has not finished, or one that has. */
  | 'net-cancel'
  /** Send the ready flag NOT already shown on the row — `net.local.ready` is
   *  the server's answer, so the request is always its inverse. */
  | 'net-ready'
  /** Ask for the other team. The server may refuse; the roster is the truth. */
  | 'net-team'
  /** Leave the room and disconnect, deliberately. */
  | 'net-leave'
  /** Put `net.code` on the clipboard. */
  | 'net-copy-code'
  /** Claim the seat the server is still holding. */
  | 'net-reconnect'
  // --- garage ---
  /**
   * The profile's loadout moved: persist it, and rebuild the preview car.
   *
   * One effect for every kind of garage edit — equip, unequip, recolour, switch
   * preset — because the view's response to all four is identical and a
   * finer-grained set would be four names for "save and redraw".
   */
  | 'loadout-changed'
  // --- progression ---
  /** Re-read the calendar: expire what has run out, add what has arrived. */
  | 'progression-refresh'
  /** Buy `shopSelection`. The runtime checks the Credits; the row only asks. */
  | 'shop-buy'
  /** Claim `passTier` on the free or the premium track. */
  | 'pass-claim-free'
  | 'pass-claim-premium'
  | 'pass-claim-all'
  /** Spend `PASS_PREMIUM_COST` EARNED Credits on the premium track. */
  | 'pass-unlock'
  /** Claim `challengeSelection`. */
  | 'challenge-claim'
  | 'challenge-claim-all'
  // --- account ---
  /** Sign in with `accountEmail`/`accountPassword`. The runtime owns the call. */
  | 'account-login'
  /** Register `accountEmail`/`accountName`/`accountPassword`. */
  | 'account-register'
  /** Ask for a fresh verification mail. */
  | 'account-resend'
  /** Ask for a password-reset mail for `accountEmail`. */
  | 'account-forgot'
  | 'account-logout';

/**
 * What a garage row asked to change.
 *
 * A REQUEST, not a change: `ProgressionRuntime` owns the profile and is the only
 * thing that may write one. The model records what was pressed and raises
 * `loadout-changed`; the view applies it and hands back a new mirror. Same
 * split as `shopSelection`, and for the same reason — see `garageEdit`.
 */
export type GarageEdit =
  | { kind: 'equip'; team: TeamSide; slot: CosmeticSlot; instanceId: string | null }
  | { kind: 'colors'; team: TeamSide; primary: string; accent: string }
  | { kind: 'preset'; id: string }
  | { kind: 'add-preset' };

export interface MatchResult {
  score: readonly [number, number];
  /** null on a draw (only reachable with overtime off). */
  winner: TeamSide | null;
  playerTeam: TeamSide;
  overtime: boolean;
  goals: readonly { team: TeamSide; clockRemaining: number; speed: number }[];
  /**
   * What the local player's car did, for the progression award.
   *
   * Optional because a NETWORKED match does not produce one: the client cannot
   * be trusted with its own stats and the server does not award progression
   * yet, so the field is absent rather than being filled with a number this
   * side made up. See `WorldEvents.detail` in src/main.ts.
   */
  stats?: MatchStats;
}

/** Things the running match tells the menu about. */
export type WorldEvent = 'countdown-complete' | 'match-ended' | 'drill-finished';

// ---------------------------------------------------------------------------
// Camera slider table
// ---------------------------------------------------------------------------

/**
 * Step and precision per camera slider. The min/max/default are NOT here —
 * they come from `CAMERA_SETTING_RANGES`, which is the single source of truth
 * for them and is also what clamps every write.
 */
export const CAMERA_SLIDERS: readonly {
  key: keyof CameraSettings;
  label: string;
  step: number;
  decimals: number;
  suffix: string;
}[] = [
  { key: 'fov', label: 'Field of View', step: 1, decimals: 0, suffix: '°' },
  { key: 'distance', label: 'Distance', step: 5, decimals: 0, suffix: '' },
  { key: 'height', label: 'Height', step: 5, decimals: 0, suffix: '' },
  { key: 'angle', label: 'Angle', step: 0.5, decimals: 2, suffix: '°' },
  { key: 'stiffness', label: 'Stiffness', step: 0.05, decimals: 2, suffix: '' },
  { key: 'swivelSpeed', label: 'Swivel Speed', step: 0.1, decimals: 2, suffix: '' },
  { key: 'transitionSpeed', label: 'Transition Speed', step: 0.05, decimals: 2, suffix: '' },
];

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

const findChoice = <T extends string | number | boolean>(
  choices: readonly Choice<T>[],
  value: T
): Choice<T> | undefined => choices.find(c => c.value === value);

/**
 * Next value in a choice list, wrapping. `implementedOnly` skips locked
 * entries so a cycler can never land on one — the locked options are still
 * shown, on the screen that presents them as a list.
 */
export const cycleChoice = <T extends string | number | boolean>(
  choices: readonly Choice<T>[],
  current: T,
  direction: 1 | -1,
  implementedOnly: boolean
): T => {
  const pool = implementedOnly ? choices.filter(c => c.implemented) : choices;
  if (pool.length === 0) return current;
  const at = pool.findIndex(c => c.value === current);
  if (at < 0) return pool[0].value;
  const next = (at + direction + pool.length) % pool.length;
  return pool[next].value;
};

/**
 * Step a slider and snap to the grid. Adding a float step repeatedly walks off
 * the grid (0.45 + 0.05 = 0.5000000000000001), which then renders as a value
 * the player cannot reproduce and cannot get back to.
 */
export const stepSlider = (value: number, step: number, direction: 1 | -1): number => {
  const raw = value + step * direction;
  return Number((Math.round(raw / step) * step).toFixed(4));
};

const formatNumber = (value: number, decimals: number, suffix: string): string =>
  `${value.toFixed(decimals)}${suffix}`;

/**
 * The note under the focused bot-skill row: what the selected tier does, plus
 * the tiers you cannot pick and why.
 *
 * The cycler skips locked tiers, which would otherwise make `learned` invisible
 * rather than visibly locked — and "the option quietly vanished" is exactly the
 * outcome the `implemented` flag exists to avoid. Naming it here is the cheapest
 * honest answer, and it is driven off the table rather than hard-coded, so a
 * checkpoint appearing removes this text by itself.
 */
const difficultyNote = (
  choices: readonly Choice<BotDifficulty>[],
  current: Choice<BotDifficulty> | undefined
): string | undefined => {
  const locked = choices.filter(c => !c.implemented);
  const parts = [
    current?.note ?? '',
    ...locked.map(c => `${c.label} is locked — ${c.note ?? 'unavailable.'}`),
  ];
  const text = parts.filter(p => p.length > 0).join('  ·  ');
  return text.length > 0 ? text : undefined;
};

/**
 * Every sub-screen ends with an explicit Back row. Escape does the same thing
 * and is faster, but a screen whose every other row is a locked mutator would
 * otherwise have nothing at all to press — and a mouse has no Escape key.
 */
const backRow = (): MenuItem => ({ kind: 'action', id: 'back', label: 'Back', enabled: true });

// ---------------------------------------------------------------------------
// Model
// ---------------------------------------------------------------------------

export interface BindTarget {
  action: BindableAction;
  slot: number;
}

/** What the modal text prompt is currently editing. */
export interface TextTarget {
  field: TextFieldId;
  /** True when committing this field should immediately connect. */
  connectOnCommit: boolean;
}

/** What a `net-connect` effect is asking the runtime for. */
export interface MenuNetIntent {
  mode: MultiplayerMode;
  code: string | null;
  create: boolean;
}

/** Things a live session tells the menu about. */
export type NetUiEvent =
  /** Seated in a room that has not started. */
  | 'lobby'
  /** In a queue. */
  | 'queued'
  /** The match is running. */
  | 'started'
  /** It ended, cleanly or otherwise. `net.failure` says which. */
  | 'closed';

export class MenuModel {
  /** Live settings. Mutated in place; `settings-changed` says when to persist. */
  settings: GameSettings;

  /** Innermost screen last. Never empty. */
  private stack: Screen[] = ['welcome'];
  private readonly focusByScreen = new Map<Screen, number>();

  /** Set while the controls screen is waiting for a key press. */
  bindTarget: BindTarget | null = null;
  /** Set while the text prompt is open. */
  textTarget: TextTarget | null = null;
  /** What has been typed into the prompt so far. */
  textBuffer = '';
  /** Transient one-line message shown under the current screen's title. */
  toast = '';

  result: MatchResult | null = null;

  // -------------------------------------------------------------------------
  // Practice state
  // -------------------------------------------------------------------------

  /**
   * The drill a `start-drill` effect is asking for. Read by the view when it
   * builds the session and by `drillresult` when it offers Run Again, so the
   * two cannot disagree about which drill was just played.
   */
  selectedDrillId: string | null = null;

  /** The last finished run's tally. Null until one finishes. */
  drillSummary: DrillSummary | null = null;

  // -------------------------------------------------------------------------
  // Ranked state
  // -------------------------------------------------------------------------

  /**
   * Which ladder the ranked screen is showing.
   *
   * Not a stored setting: it is a thing you look at, not a thing you configure,
   * and a persisted value would mean a player who once looked at 3v3 opens the
   * screen on a mode they do not play. It starts on whatever mode they last
   * QUEUED for, which is the one they actually care about.
   */
  rankedMode: MultiplayerMode = 'duel';

  // -------------------------------------------------------------------------
  // Multiplayer state, written by the view
  // -------------------------------------------------------------------------

  /**
   * The live connection, as the runtime last saw it. A MIRROR — see the header
   * of ./net-model. The model never writes to it and never predicts it.
   */
  net: NetView = idleNetView();

  /** What the last `net-connect` effect is asking for. */
  netIntent: MenuNetIntent | null = null;

  /** True while the current match is a networked one, which changes what the
   *  pause and full-time screens are allowed to offer. */
  netMatch = false;

  // -------------------------------------------------------------------------
  // Training state, written by the view
  // -------------------------------------------------------------------------

  /**
   * Whether a compatible checkpoint exists. THE one thing that unlocks the
   * `learned` tier — see `difficultyChoicesFor` in src/game/settings.ts. False
   * until something has actually looked in the store and found one, because the
   * answer is asynchronous and "not yet known" must behave like "not there".
   */
  learnedAvailable = false;

  /** Mirrors the trainer's phase. The model never changes this itself. */
  trainingPhase: TrainerPhase = 'idle';
  /** PPO updates completed in the current run. Gates "save a checkpoint". */
  trainingIteration = 0;
  /** The store's contents, newest first, as the view last read them. */
  checkpoints: readonly CheckpointRow[] = [];
  /** Which checkpoint the detail screen is about. */
  selectedCheckpointId: string | null = null;

  // -------------------------------------------------------------------------
  // Progression state, written by the view
  // -------------------------------------------------------------------------

  /**
   * The profile, the claim ledger and the day's shop, as `ProgressionRuntime`
   * last saw them. A MIRROR, exactly like `net` — the model never loads it,
   * never writes it and never reads a clock to build it.
   *
   * Null until the runtime hands one over, which in the shipped game is inside
   * the overlay's constructor. The three screens degrade to a single Back row
   * and a note rather than pretending to a shop they have not got.
   */
  progression: ProgressionState | null = null;

  /** Which tier the pass screen is showing. Moved with left/right. */
  passTier = 1;

  // -------------------------------------------------------------------------
  // Garage state
  // -------------------------------------------------------------------------

  /**
   * Which team colour the garage is editing. NOT the same as `setup.team`: a
   * player may want to fix their orange loadout while queued as blue, and Rocket
   * League lets them.
   */
  garageTeam: TeamSide = 0;

  /** Which slot the picker is about, or null when it is not open. */
  garageSlot: CosmeticSlot | null = null;

  /**
   * What a garage row asked to change, for the `loadout-changed` effect.
   *
   * Recorded rather than applied, exactly like `shopSelection`: the profile
   * belongs to `ProgressionRuntime` and the model may not write to it, or the
   * exhaustive navigation search would be equipping items into localStorage.
   */
  garageEdit: GarageEdit | null = null;

  // -------------------------------------------------------------------------
  // Account state
  // -------------------------------------------------------------------------

  /**
   * The account session, as `AccountRuntime` last saw it. A MIRROR, exactly
   * like `net` and `progression`. Null until the runtime hands one over.
   */
  account: AccountView | null = null;

  /**
   * The sign-in form. Plain in-memory strings, deliberately NOT settings: a
   * password must never ride `saveSettings` into localStorage, and an email
   * typed once belongs to this visit to the screen, not to the machine.
   */
  accountEmail = '';
  accountPassword = '';
  accountName = '';

  /** Which shop row was last confirmed, for the `shop-buy` effect. */
  shopSelection: ShopRow | null = null;

  /** Which challenge was last confirmed, for the `challenge-claim` effect. */
  challengeSelection: string | null = null;

  constructor(settings: GameSettings, learnedAvailable = false) {
    this.settings = settings;
    this.learnedAvailable = learnedAvailable;
  }

  /**
   * Replace the mirrored progression.
   *
   * Deliberately does NOT navigate, for the same reason `setNetView` does not:
   * a weekly expiring while the player is reading the camera screen must change
   * what the challenge screen says and must not move them.
   *
   * The tier cursor is clamped rather than reset, so claiming a tier leaves the
   * player looking at the tier they claimed.
   */
  setProgression(state: ProgressionState | null): void {
    this.progression = state;
    if (state !== null) this.passTier = this.clampTier(this.passTier, state);
  }

  /**
   * Replace the mirrored account state. Deliberately does NOT navigate, for
   * the same reason `setNetView` does not — a session expiring while the
   * player reads the camera screen must not move them. The password buffer is
   * cleared the moment a session exists; there is no reason to keep it.
   */
  setAccount(view: AccountView | null): void {
    const was = this.signedIn;
    this.account = view;
    if (view?.phase === 'signedIn') this.accountPassword = '';

    // The ONE navigation this is allowed to do, and it is not an exception to
    // the rule above so much as the other side of it. `welcome`, `signin` and
    // `register` exist for exactly one purpose, and a session arriving is that
    // purpose being served — leaving the player on a sign-in form they have
    // just finished with would be the bug. Every other screen is left alone,
    // including a session that turns up while somebody is reading Controls.
    //
    // It is also what makes a restored token invisible: `AccountRuntime` calls
    // /api/me at startup, and the player sees the main menu, not a login
    // prompt that answers itself a moment later.
    if (!was && this.signedIn && ENTRY_SCREENS.includes(this.screen)) {
      this.reset('main');
    }
  }

  /**
   * What the account screens say under their title, and whether it went well.
   *
   * Null everywhere else, and null before anything has been attempted, so the
   * fixed subtitle stands until there is news.
   *
   * This exists because the answer used to be invisible. The message was only
   * ever rendered as the note of the focused row WHILE BUSY, so every outcome
   * — account created, email already registered, password too short, server
   * unreachable — was replaced by static text in the same frame it arrived.
   * Pressing Create Account and having absolutely nothing happen was not a
   * failure to send the request; it was the answer landing somewhere nobody
   * could see. A status line under the title is the smallest thing that cannot
   * do that: it is not tied to focus, and it does not clear on the next
   * keypress the way a toast does.
   */
  get accountStatus(): { text: string; tone: AccountTone } | null {
    if (!ACCOUNT_SCREENS.includes(this.screen)) return null;
    const view = this.account;
    if (view === null || view.message.length === 0) return null;
    return { text: view.message, tone: view.tone };
  }

  /** True when the player has a live signed-in session. THE gate: multiplayer,
   *  the garage and every progression screen require it, because everything
   *  they show lives in the server's database. */
  get signedIn(): boolean {
    return this.account?.phase === 'signedIn';
  }

  /** 1 .. (highest reached + lookahead), never below the seventy base tiers. */
  private tierCeiling(state: ProgressionState): number {
    return Math.max(PASS_TIERS, state.profile.progress.passTier + PASS_LOOKAHEAD);
  }

  private clampTier(tier: number, state: ProgressionState): number {
    return Math.min(Math.max(1, Math.round(tier)), this.tierCeiling(state));
  }

  /**
   * Tell the model what the trainer is doing. Called from the view's progress
   * callback; returns nothing, because a phase change never navigates — a run
   * finishing under a menu the player has walked away from must not yank them
   * back to the training screen.
   */
  setTrainingPhase(phase: TrainerPhase, iteration: number): void {
    this.trainingPhase = phase;
    this.trainingIteration = iteration;
  }

  /**
   * Replace the mirrored connection state.
   *
   * Deliberately does NOT navigate, for the same reason `setTrainingPhase` does
   * not: a ping update or a roster change arriving while the player is reading
   * the camera screen must not yank them anywhere. Navigation happens only on
   * the four discrete moments in `notifyNet`, which the runtime raises
   * explicitly.
   */
  setNetView(view: NetView): void {
    this.net = view;
  }

  /** Replace the checkpoint list, keeping the detail screen's selection valid. */
  setCheckpoints(rows: readonly CheckpointRow[]): void {
    this.checkpoints = rows;
    if (this.selectedCheckpointId !== null && !rows.some(r => r.id === this.selectedCheckpointId)) {
      // The one it was about is gone — deleted here or in another tab. Pop the
      // detail screen rather than leave it describing nothing.
      this.selectedCheckpointId = null;
      if (this.screen === 'checkpoint') this.stack.pop();
    }
  }

  /** The row the checkpoint detail screen is describing, if any. */
  get selectedCheckpoint(): CheckpointRow | null {
    return this.checkpoints.find(r => r.id === this.selectedCheckpointId) ?? null;
  }

  // -------------------------------------------------------------------------
  // Read side
  // -------------------------------------------------------------------------

  get screen(): Screen {
    return this.stack[this.stack.length - 1];
  }

  get breadcrumb(): readonly Screen[] {
    return this.stack;
  }

  /** True while the fixed loop should be ticking the world. */
  get simRunning(): boolean {
    return LIVE_SCREENS.includes(this.screen);
  }

  /** True when a world exists at all (running, paused, or being scored). */
  get matchActive(): boolean {
    return this.stack.some(s => LIVE_SCREENS.includes(s));
  }

  /** False only during play, when the overlay must be completely out of the way. */
  get overlayVisible(): boolean {
    return this.screen !== 'match' && this.screen !== 'netmatch';
  }

  /**
   * The screen the view should DRAW, which is not always the screen you are
   * ON. Both prompts are modals: the list underneath has to stay visible or you
   * cannot see which row you are rebinding, or which field you are typing into.
   */
  get paintScreen(): Screen {
    if (this.screen === 'bind') return this.stack[this.stack.length - 2] ?? 'controls';
    if (this.screen === 'text') return this.stack[this.stack.length - 2] ?? 'multiplayer';
    return this.screen;
  }

  get items(): MenuItem[] {
    return this.itemsFor(this.screen);
  }

  focusIndexFor(screen: Screen): number {
    const count = this.itemsFor(screen).length;
    if (count === 0) return 0;
    const raw = this.focusByScreen.get(screen) ?? 0;
    return Math.min(Math.max(0, raw), count - 1);
  }

  get focusIndex(): number {
    return this.focusIndexFor(this.screen);
  }

  get focusedItem(): MenuItem | null {
    return this.items[this.focusIndex] ?? null;
  }

  // -------------------------------------------------------------------------
  // Item construction
  // -------------------------------------------------------------------------

  itemsFor(screen: Screen): MenuItem[] {
    switch (screen) {
      case 'main': {
        // Everything the database owns — multiplayer, the garage, the shop,
        // the pass, the challenges — is locked until there is a session to own
        // it FOR. The locked note says where the key is rather than just "no".
        const signedIn = this.signedIn;
        const gate = {
          enabled: signedIn,
          lockLabel: 'Sign In',
          ...(signedIn
            ? {}
            : { note: 'Requires an account — everything you earn lives on the server. Sign in under Account.' }),
        };
        const user = this.account?.user ?? null;
        return [
          { kind: 'action', id: 'play', label: 'Play', enabled: true, emphasis: true },
          {
            kind: 'action',
            id: 'multiplayer',
            label: 'Multiplayer',
            ...gate,
            ...(signedIn ? { note: 'Play against other people on a dedicated server.' } : {}),
          },
          {
            kind: 'action',
            id: 'ranked',
            label: 'Ranked',
            // NOT gated behind an account, unlike the rows below it. The
            // leaderboard is public (see /api/ranked/leaderboard), and the
            // ladder is the best argument the game has for making an account —
            // which it cannot make from behind a Sign In chip.
            enabled: true,
            note: 'The ladder, your standing in it, and the matches behind it.',
          },
          {
            kind: 'action',
            id: 'drills',
            label: 'Practice',
            enabled: true,
            note: 'Ten attempts at one shot: aerials, wall reads, kickoffs. No opponent, no clock.',
          },
          {
            kind: 'action',
            id: 'training',
            label: 'Training',
            enabled: true,
            note: 'Train a bot in this tab with reinforcement learning, then play against it.',
          },
          {
            kind: 'action',
            id: 'garage',
            label: 'Garage',
            ...gate,
            ...(signedIn
              ? { note: 'Paint, decals, wheels, boost, a number and a flag — on a live preview of the car.' }
              : {}),
          },
          {
            kind: 'action',
            id: 'shop',
            label: 'Item Shop',
            ...gate,
            ...(signedIn
              ? { note: 'A featured and a daily rotation, refreshed every day. Credits are earned by playing.' }
              : {}),
          },
          {
            kind: 'action',
            id: 'rocketpass',
            label: 'Rocket Pass',
            ...gate,
            ...(signedIn
              ? { note: 'Seventy tiers and then Pro Tiers, on a free and a premium track.' }
              : {}),
          },
          {
            kind: 'action',
            id: 'challenges',
            label: 'Challenges',
            ...gate,
            ...(signedIn
              ? { note: 'Weekly, Season and Event. Each one finished is Tier Points on the pass.' }
              : {}),
          },
          {
            kind: 'action',
            id: 'account',
            label: user === null ? 'Account — Sign In' : `Account — ${user.displayName}`,
            enabled: true,
            emphasis: !signedIn,
            note:
              user === null
                ? 'Create an account or sign in. Your items, credits and progress live on the server.'
                : `Signed in as ${user.displayName} (${user.email}).`,
          },
          { kind: 'action', id: 'camera', label: 'Camera', enabled: true },
          { kind: 'action', id: 'audio', label: 'Audio', enabled: true },
          { kind: 'action', id: 'controls', label: 'Controls', enabled: true },
        ];
      }

      case 'mode':
        return [
          ...MODE_CHOICES.map(choice => ({
            kind: 'action' as const,
            id: `mode:${choice.value}`,
            label: choice.label,
            enabled: choice.implemented,
            note: choice.note,
          })),
          backRow(),
        ];

      case 'setup':
        return this.setupItems();

      case 'mutators':
        return this.mutatorItems();

      case 'camera':
        return this.cameraItems();

      case 'audio':
        return this.audioItems();

      case 'controls':
        return this.controlItems();

      case 'drills':
        return [
          ...DRILLS.map(drill => ({
            kind: 'action' as const,
            id: `${DRILL_PREFIX}${drill.id}`,
            label: drill.name,
            enabled: true,
            // Both halves, in one note: what appears on the pitch, then what it
            // is for. Six two-word names tell a new player nothing about which
            // to pick, and the blurb alone tells them nothing about why.
            note: `${drill.blurb} ${drill.teaches}`,
          })),
          backRow(),
        ];

      case 'ranked':
        return [
          {
            kind: 'choice',
            id: 'ranked-mode',
            label: 'Leaderboard',
            valueLabel:
              MULTIPLAYER_MODE_CHOICES.find(c => c.value === this.rankedMode)?.label ??
              this.rankedMode,
            index: Math.max(0, MULTIPLAYER_MODE_CHOICES.findIndex(c => c.value === this.rankedMode)),
            count: MULTIPLAYER_MODE_CHOICES.length,
            enabled: true,
            note: 'Which ladder to show. Your own standing is listed for every mode you have played.',
          },
          backRow(),
        ];

      case 'drillresult':
        return this.drillResultItems();

      case 'training':
        return this.trainingItems();

      case 'checkpoints':
        return this.checkpointItems();

      case 'checkpoint':
        return this.checkpointDetailItems();

      case 'garage':
        return this.garageItems();

      case 'garageSlot':
        return this.garageSlotItems();

      case 'garagePresets':
        return this.garagePresetItems();

      case 'shop':
        return this.shopItems();

      case 'rocketpass':
        return this.passItems();

      case 'challenges':
        return this.challengeItems();

      case 'welcome':
        return this.welcomeItems();

      case 'account':
        return this.accountItems();

      case 'signin':
        return this.signinItems();

      case 'register':
        return this.registerItems();

      case 'multiplayer':
        return this.multiplayerItems();

      case 'netmode':
        return [
          ...MULTIPLAYER_MODE_CHOICES.map(choice => ({
            kind: 'action' as const,
            id: `${NET_MODE_PREFIX}${choice.value}`,
            label: choice.label,
            enabled: choice.implemented,
            note: choice.note,
          })),
          backRow(),
        ];

      case 'connecting':
        return this.connectingItems();

      case 'lobby':
        return this.lobbyItems();

      case 'netpause':
        return [
          {
            kind: 'action',
            id: 'resume',
            label: 'Resume',
            enabled: true,
            emphasis: true,
            // Said out loud, because every other pause menu in every other game
            // means the opposite and a player who assumes so gets scored on.
            note: 'The match never stopped — the server is still simulating it.',
          },
          { kind: 'action', id: 'camera', label: 'Camera', enabled: true },
          { kind: 'action', id: 'audio', label: 'Audio', enabled: true },
          { kind: 'action', id: 'controls', label: 'Controls', enabled: true },
          {
            kind: 'action',
            id: 'net-leave',
            label: 'Leave Match',
            enabled: true,
            note: 'Your seat is held for thirty seconds, so you can come back.',
          },
        ];

      case 'disconnected':
        return this.disconnectedItems();

      case 'pause':
        return [
          { kind: 'action', id: 'resume', label: 'Resume', enabled: true, emphasis: true },
          { kind: 'action', id: 'camera', label: 'Camera', enabled: true },
          { kind: 'action', id: 'audio', label: 'Audio', enabled: true },
          { kind: 'action', id: 'controls', label: 'Controls', enabled: true },
          { kind: 'action', id: 'restart', label: 'Restart Match', enabled: true },
          { kind: 'action', id: 'quit', label: 'Quit to Menu', enabled: true },
        ];

      case 'postmatch':
        // A networked match cannot be rematched or reconfigured from here: the
        // room is the server's and it is over. Offering a Rematch row that
        // rebuilt a LOCAL match under the same heading would be the worst kind
        // of lie — one the player only notices when the bots turn out to be bots.
        return this.netMatch
          ? [
              {
                kind: 'action',
                id: 'net-again',
                label: 'Find Another Match',
                enabled: true,
                emphasis: true,
              },
              { kind: 'action', id: 'quit', label: 'Main Menu', enabled: true },
            ]
          : [
              { kind: 'action', id: 'rematch', label: 'Rematch', enabled: true, emphasis: true },
              { kind: 'action', id: 'change-setup', label: 'Change Setup', enabled: true },
              { kind: 'action', id: 'quit', label: 'Main Menu', enabled: true },
            ];

      // No rows: the world owns the timing, and the two prompts own the keyboard.
      case 'bind':
      case 'text':
      case 'countdown':
      case 'match':
      case 'netmatch':
        return [];
    }
  }

  /** The tier table as it stands right now — `learned` included only if a
   *  checkpoint backs it. Read through this everywhere, never off the raw
   *  constant, so the cycler, the value label and the note cannot disagree. */
  private get difficultyChoices(): readonly Choice<BotDifficulty>[] {
    return difficultyChoicesFor(this.learnedAvailable);
  }

  private setupItems(): MenuItem[] {
    const setup = this.settings.setup;
    const implementedModes = MODE_CHOICES.filter(c => c.implemented);
    // Free play has no opponent, no clock and no result, which takes three of
    // the rows below out of service. Computed once and named, because the
    // alternative is the same mode test written three times.
    const solo = setup.mode === 'freeplay';
    const tiers = this.difficultyChoices;
    const implementedTiers = tiers.filter(c => c.implemented);
    const mode = findChoice(MODE_CHOICES, setup.mode);
    const difficulty = findChoice(tiers, setup.difficulty);
    const team = findChoice(TEAM_CHOICES, setup.team);
    const car = findChoice(CAR_CHOICES, setup.car);
    const length = findChoice(LENGTH_CHOICES, setup.length);

    return [
      {
        kind: 'choice',
        id: 'mode',
        label: 'Mode',
        valueLabel: mode?.label ?? '—',
        index: implementedModes.findIndex(c => c.value === setup.mode),
        count: implementedModes.length,
        enabled: true,
        note: mode?.note,
      },
      {
        kind: 'choice',
        id: 'difficulty',
        label: 'Bot Skill',
        valueLabel: difficulty?.label ?? '—',
        index: implementedTiers.findIndex(c => c.value === setup.difficulty),
        count: implementedTiers.length,
        // Free play has no bots to be skilful. A row that still cycled here
        // would be adjusting a number nothing reads — the same lie a locked
        // row exists to avoid telling.
        enabled: !solo,
        lockLabel: solo ? 'N/A' : undefined,
        note: solo ? 'No bots in Free Play.' : difficultyNote(tiers, difficulty),
      },
      {
        kind: 'choice',
        id: 'team',
        label: 'Team',
        valueLabel: team?.label ?? '—',
        index: TEAM_CHOICES.findIndex(c => c.value === setup.team),
        count: TEAM_CHOICES.length,
        enabled: true,
      },
      {
        kind: 'choice',
        id: 'car',
        label: 'Car',
        valueLabel: car?.label ?? '—',
        index: CAR_CHOICES.findIndex(c => c.value === setup.car),
        count: CAR_CHOICES.length,
        enabled: true,
        note: car?.note,
      },
      {
        kind: 'choice',
        id: 'length',
        label: 'Match Length',
        // `matchConfigFor` overrides both of these in free play, so the rows
        // report what the match will ACTUALLY run with rather than the stored
        // value they are ignoring.
        valueLabel: solo ? 'Unlimited' : (length?.label ?? '—'),
        index: solo ? 0 : LENGTH_CHOICES.findIndex(c => c.value === setup.length),
        count: solo ? 1 : LENGTH_CHOICES.length,
        enabled: !solo,
        lockLabel: solo ? 'N/A' : undefined,
        note: solo ? 'Free Play does not run out.' : length?.note,
      },
      {
        kind: 'toggle',
        id: 'overtime',
        label: 'Overtime',
        on: solo ? false : setup.overtime,
        enabled: !solo,
        lockLabel: solo ? 'N/A' : undefined,
        note: solo ? 'Nothing to go to overtime over.' : undefined,
      },
      { kind: 'action', id: 'mutators', label: 'Mutators', enabled: true },
      { kind: 'action', id: 'camera', label: 'Camera', enabled: true },
      { kind: 'action', id: 'audio', label: 'Audio', enabled: true },
      { kind: 'action', id: 'controls', label: 'Controls', enabled: true },
      {
        kind: 'action',
        id: 'start',
        label: solo ? 'Start Free Play' : 'Start Match',
        enabled: true,
        emphasis: true,
      },
    ];
  }

  private mutatorItems(): MenuItem[] {
    const mutators = this.settings.setup.mutators as unknown as Record<string, string>;
    const rows: MenuItem[] = MUTATOR_ROWS.map(row => {
      const value = mutators[row.key];
      const index = row.choices.findIndex(c => c.value === value);
      const choice = row.choices[Math.max(0, index)];
      return {
        kind: 'choice' as const,
        id: `${MUTATOR_PREFIX}${row.key}`,
        label: row.label,
        valueLabel: choice?.label ?? '—',
        index: Math.max(0, index),
        count: row.choices.length,
        // Per-row, because game speed is a driver setting rather than a
        // physics one and could plausibly land separately from the rest.
        enabled: choice?.implemented ?? false,
        note: choice?.implemented ? undefined : 'Not wired into the simulation yet.',
      };
    });
    return [...rows, backRow()];
  }

  private cameraItems(): MenuItem[] {
    const camera = this.settings.camera;
    const sliders: MenuItem[] = CAMERA_SLIDERS.map(slider => {
      const range = CAMERA_SETTING_RANGES[slider.key];
      const value = camera[slider.key];
      return {
        kind: 'slider' as const,
        id: `cam:${slider.key}`,
        label: slider.label,
        value,
        min: range.min,
        max: range.max,
        valueLabel: formatNumber(value, slider.decimals, slider.suffix),
        enabled: true,
      };
    });
    return [
      ...sliders,
      {
        kind: 'toggle',
        id: 'ballcam',
        label: 'Ball Cam At Kickoff',
        on: this.settings.ballCam,
        enabled: true,
        note: 'Toggle it mid-match with the Ball Cam key.',
      },
      { kind: 'action', id: 'reset-camera', label: 'Restore Defaults', enabled: true },
      backRow(),
    ];
  }

  /**
   * Volume and mute.
   *
   * Muting does NOT zero the slider, and the slider stays live while muted:
   * they are separate intentions, the mixer keeps them on separate nodes for
   * that reason, and a mute that ate your level would be a mute you stopped
   * using. The row says as much rather than leaving the player to test it.
   */
  private audioItems(): MenuItem[] {
    const audio = this.settings.audio;
    return [
      {
        kind: 'slider',
        id: 'audio:volume',
        label: 'Master Volume',
        value: audio.volume,
        min: 0,
        max: 1,
        valueLabel: `${Math.round(audio.volume * 100)}%`,
        enabled: true,
      },
      {
        kind: 'toggle',
        id: 'audio:muted',
        label: 'Mute',
        on: audio.muted,
        enabled: true,
        note: 'Keeps your volume setting.',
      },
      { kind: 'action', id: 'reset-audio', label: 'Restore Defaults', enabled: true },
      backRow(),
    ];
  }

  /**
   * The controls screen: the on-screen pair first, then the key bindings.
   *
   * The touch rows are FIRST and not on a screen of their own, because the
   * player who needs them is holding a phone and has just discovered they
   * cannot drive. Two rows above the bindings is the shortest path from that
   * discovery to the fix; a `Touch` sub-screen would be one more tap to find
   * and one more place to look.
   *
   * The throttle row locks when the controls are off, for the reason every
   * locked row in this file locks: with no on-screen buttons drawn there is
   * nothing for it to change, and a row that stores a value nothing reads is
   * exactly what `implemented: false` exists to prevent.
   */
  /**
   * The screen a finished run lands on.
   *
   * `Run It Again` first and emphasised, because that is what a player who has
   * just missed six of ten wants and the alternative is four keystrokes back
   * through a list they already chose from. It reruns the SAME drill —
   * `selectedDrillId` is not cleared by finishing.
   */
  private drillResultItems(): MenuItem[] {
    return [
      ...(this.selectedDrillId === null
        ? []
        : [
            {
              kind: 'action' as const,
              id: 'drill-again',
              label: 'Run It Again',
              enabled: true,
              emphasis: true,
            },
          ]),
      { kind: 'action', id: 'drill-list', label: 'Another Drill', enabled: true },
      { kind: 'action', id: 'quit', label: 'Main Menu', enabled: true },
    ];
  }

  private controlItems(): MenuItem[] {
    const touch = this.settings.touch;
    const modeIndex = Math.max(0, TOUCH_MODE_CHOICES.findIndex(c => c.value === touch.mode));
    const throttleIndex = touch.autoThrottle ? 0 : 1;
    const touchOff = touch.mode === 'off';

    const touchRows: MenuItem[] = [
      {
        kind: 'choice',
        id: `${TOUCH_PREFIX}mode`,
        label: 'On-Screen Controls',
        valueLabel: TOUCH_MODE_CHOICES[modeIndex]?.label ?? '—',
        index: modeIndex,
        count: TOUCH_MODE_CHOICES.length,
        enabled: true,
        note: TOUCH_MODE_CHOICES[modeIndex]?.note,
      },
      {
        kind: 'choice',
        id: `${TOUCH_PREFIX}autoThrottle`,
        label: 'Touch Throttle',
        valueLabel: AUTO_THROTTLE_CHOICES[throttleIndex]?.label ?? '—',
        index: throttleIndex,
        count: AUTO_THROTTLE_CHOICES.length,
        enabled: !touchOff,
        lockLabel: 'No Touch',
        note: touchOff
          ? 'Nothing to throttle: the on-screen controls are off.'
          : AUTO_THROTTLE_CHOICES[throttleIndex]?.note,
      },
    ];

    const rows: MenuItem[] = BINDING_ROWS.map(row => ({
      kind: 'bind' as const,
      id: `bind:${row.action}`,
      action: row.action,
      label: row.label,
      slots: slotLabels(this.settings.bindings, row.action),
      slot: this.bindSlot,
      enabled: true,
    }));
    return [
      ...touchRows,
      ...rows,
      { kind: 'action', id: 'reset-bindings', label: 'Restore Defaults', enabled: true },
      backRow(),
    ];
  }

  /**
   * The training screen.
   *
   * The hyperparameter rows lock while a run is live rather than pretending to
   * take effect: `TrainingConfig` is read once when a `Trainer` is constructed,
   * so a learning rate nudged mid-run would move a number on screen and change
   * nothing at all. That is the exact failure the `enabled`/`note` convention
   * exists to prevent, and it applies to a live run as much as to a missing
   * feature.
   */
  private trainingItems(): MenuItem[] {
    const phase = this.trainingPhase;
    const live = isTrainingLive(phase);
    const training = this.settings.training as unknown as Record<string, string | number>;

    const settingRows: MenuItem[] = TRAINING_ROWS.map(row => {
      const value = training[row.key];
      const index = row.choices.findIndex(c => c.value === value);
      const choice = row.choices[Math.max(0, index)];
      return {
        kind: 'choice' as const,
        id: `${TRAINING_PREFIX}${row.key}`,
        label: row.label,
        valueLabel: choice?.label ?? '—',
        index: Math.max(0, index),
        count: row.choices.length,
        enabled: !live,
        lockLabel: 'Running',
        note: live ? 'Stop the run to change this — a trainer reads its config once.' : (choice?.note ?? row.note),
      };
    });

    return [
      {
        kind: 'action',
        id: 'train-run',
        label: runRowLabel(phase),
        enabled: true,
        emphasis: true,
        // Honest about the half that is NOT off this thread. "Runs in Web
        // Workers" on its own reads as "the page stays smooth", and it does
        // not: the PPO update blocks, and `UI Freeze` on the panel says by how
        // much.
        note: isTrainingActive(phase)
          ? 'Rollouts run in Web Workers; the PPO update runs here — see UI Freeze.'
          : 'Rollouts run in Web Workers. The PPO update runs on this thread and blocks it.',
      },
      {
        kind: 'action',
        id: 'train-stop',
        label: 'Stop',
        enabled: live,
        lockLabel: 'Idle',
        note: live
          ? 'Ends the run. Save a checkpoint first if you want to keep it.'
          : 'Nothing is running.',
      },
      {
        kind: 'toggle',
        id: 'train-watch',
        label: 'Watch Live',
        on: this.settings.training.watchLive,
        enabled: true,
        note: 'Play the newest policy in a match behind this menu, refreshed each iteration.',
      },
      {
        kind: 'action',
        id: 'checkpoints',
        label: 'Checkpoints',
        enabled: true,
        note: `${this.checkpoints.length} saved.`,
      },
      ...settingRows,
      backRow(),
    ];
  }

  /** The checkpoint store, as a list. Empty is a normal state, not an error. */
  private checkpointItems(): MenuItem[] {
    const rows: MenuItem[] = this.checkpoints.map(row => ({
      kind: 'action' as const,
      id: `${CHECKPOINT_PREFIX}${row.id}`,
      label: checkpointRowLabel(row),
      enabled: true,
    }));
    return [
      {
        kind: 'action',
        id: 'ckpt-save',
        label: 'Save Checkpoint',
        enabled: this.trainingIteration > 0,
        lockLabel: 'Empty',
        note:
          this.trainingIteration > 0
            ? `Writes the current network, at iteration ${this.trainingIteration}.`
            : 'Train at least one iteration first — there is nothing to save yet.',
      },
      {
        kind: 'action',
        id: 'ckpt-import',
        label: 'Import From File',
        enabled: true,
        note: 'Reads a checkpoint JSON written here or by the Node trainer.',
      },
      ...rows,
      backRow(),
    ];
  }

  private checkpointDetailItems(): MenuItem[] {
    const selected = this.selectedCheckpoint;
    return [
      {
        kind: 'action',
        id: 'ckpt-load',
        label: 'Load',
        enabled: selected !== null,
        emphasis: true,
        note: 'Becomes the Learned (RL) bot, and the starting point of the next run.',
      },
      {
        kind: 'action',
        id: 'ckpt-export',
        label: 'Export JSON',
        enabled: selected !== null,
        note: 'Downloads a file the Node trainer can resume from.',
      },
      {
        kind: 'action',
        id: 'ckpt-delete',
        label: 'Delete',
        enabled: selected !== null,
        note: 'Permanent. Export it first if it is any good.',
      },
      backRow(),
    ];
  }

  // -------------------------------------------------------------------------
  // Progression screens
  // -------------------------------------------------------------------------

  /**
   * The one row every progression screen falls back to.
   *
   * A screen that has no state to draw says so rather than drawing an empty
   * shop, which is the same rule the rest of this file follows: a control that
   * cannot work must not look like it worked.
   */
  private noProgression(what: string): MenuItem[] {
    return [
      {
        kind: 'action',
        id: 'progression-missing',
        label: what,
        enabled: false,
        lockLabel: 'Loading',
        note: 'Your profile has not been read yet.',
      },
      backRow(),
    ];
  }

  // -------------------------------------------------------------------------
  // Garage
  // -------------------------------------------------------------------------

  /**
   * The garage reads the SAME mirrored profile the shop and the pass do.
   *
   * One profile, one owner (`ProgressionRuntime`), one mirror — so an item
   * bought on the shop screen is equippable on the garage screen without either
   * of them knowing the other exists. Null before the runtime has handed one
   * over, which is the same "not yet known" the net and training screens have.
   */
  private garageItems(): MenuItem[] {
    const state = this.progression;
    if (state === null) return this.noProgression('Garage');
    return garageRows(state.profile, this.garageTeam);
  }

  private garageSlotItems(): MenuItem[] {
    const state = this.progression;
    if (state === null) return this.noProgression('Garage');
    const slot = this.garageSlot;
    if (slot === null) return [backRow()];
    return slotRows(state.profile, this.garageTeam, slot);
  }

  private garagePresetItems(): MenuItem[] {
    const state = this.progression;
    if (state === null) return this.noProgression('Garage');
    return presetRows(state.profile);
  }

  private shopItems(): MenuItem[] {
    const state = this.progression;
    if (state === null) return this.noProgression('Shop');

    const rows: MenuItem[] = shopRows(state).map(row => ({
      kind: 'value' as const,
      id: `${SHOP_PREFIX}${row.row}:${row.index}`,
      label: row.name,
      valueLabel: `${row.entry.price} CR`,
      badge: row.problem === 'owned' ? 'Owned' : rarityLabel(row.rarity),
      enabled: row.problem === null,
      lockLabel: row.problem === 'owned' ? 'Owned' : 'Credits',
      // Which rotation it came from is in the note and in the panel beside it,
      // not in the row's weight: four rows in heading type is a shop that
      // shouts, and `hero` is for the one action a screen is actually for.
      note: `${row.row === 'featured' ? 'Featured' : 'Daily'}  ·  ${shopRowNote(row)}`,
    }));

    return [...rows, backRow()];
  }

  /**
   * The Rocket Pass.
   *
   * A CURSOR over the tiers rather than one row per tier: there are seventy of
   * them and then Pro Tiers with no end, so a row each is not a list that can be
   * drawn or navigated. Left and right move the cursor, the two claim rows act
   * on wherever it is, and the track itself is drawn in the side panel.
   */
  private passItems(): MenuItem[] {
    const state = this.progression;
    if (state === null) return this.noProgression('Rocket Pass');

    const progress = state.profile.progress;
    const tier = this.clampTier(this.passTier, state);
    const row = passRow(state, tier);
    const premium = progress.passPremium;

    const trackRow = (
      id: string,
      label: string,
      track: typeof row.free,
      locked: boolean
    ): MenuItem => ({
      kind: 'value',
      id,
      label,
      valueLabel: passRewardLabel(track.reward),
      badge: track.claimed
        ? 'Claimed'
        : track.claimable
          ? 'Ready'
          : locked
            ? 'Premium'
            : row.reached
              ? '—'
              : 'Locked',
      enabled: track.claimable,
      lockLabel: track.claimed ? 'Claimed' : locked ? 'Premium' : row.reached ? 'Empty' : 'Locked',
      note: track.claimed
        ? 'Already claimed.'
        : track.reward === null
          ? 'Nothing on this track at this tier.'
          : locked
            ? `Unlock the premium track with ${PASS_PREMIUM_COST} earned Credits.`
            : row.reached
              ? 'Ready to claim.'
              : `${tierLabel(tier)} is not unlocked yet — ${(tier - progress.passTier) * 10} more Tier Points.`,
    });

    return [
      {
        kind: 'choice',
        id: 'pass-tier',
        label: 'Tier',
        valueLabel: tierLabel(tier),
        index: tier - 1,
        count: this.tierCeiling(state),
        enabled: true,
        note: `You are on ${tierLabel(progress.passTier)} with ${progress.tierPoints} of 10 Tier Points.`,
      },
      trackRow('pass-free', 'Free Track', row.free, false),
      trackRow('pass-premium-claim', 'Premium Track', row.premium, !premium),
      {
        kind: 'action',
        id: 'pass-claim-all',
        label: 'Claim Everything',
        enabled: true,
        emphasis: true,
        note: 'Takes every reward you have reached and not yet claimed.',
      },
      {
        kind: 'action',
        id: 'pass-unlock',
        label: premium ? 'Premium Track Owned' : `Unlock Premium · ${PASS_PREMIUM_COST} Credits`,
        enabled: !premium,
        lockLabel: 'Owned',
        // Said out loud, on the one row where a player raised on the real game
        // will be looking for a payment prompt.
        note: premium
          ? 'The premium track is yours for this season.'
          : `Costs ${PASS_PREMIUM_COST} Credits, which are earned by playing. There is no way to buy Credits here, by design.`,
      },
      backRow(),
    ];
  }

  private challengeItems(): MenuItem[] {
    const state = this.progression;
    if (state === null) return this.noProgression('Challenges');

    const rows = challengeRows(state);
    const ready = rows.filter(r => r.complete && !r.claimed && !r.expired).length;

    const items: MenuItem[] = rows.map(row => ({
      kind: 'value' as const,
      id: `${CHALLENGE_PREFIX}${row.def.id}`,
      label: this.challengeLabel(row),
      valueLabel: progressLabel(row),
      badge: row.claimed
        ? 'Claimed'
        : row.expired
          ? 'Expired'
          : row.complete
            ? 'Ready'
            : `${row.tierPoints} TP`,
      fraction: Math.min(1, row.entry.value / Math.max(1, row.def.target)),
      enabled: row.complete && !row.claimed && !row.expired,
      lockLabel: row.claimed ? 'Claimed' : row.expired ? 'Expired' : 'In Progress',
      note: challengeRowNote(row),
    }));

    return [
      {
        kind: 'action',
        id: 'chal-claim-all',
        label: 'Claim Everything',
        enabled: ready > 0,
        emphasis: ready > 0,
        lockLabel: 'None Ready',
        note:
          ready > 0
            ? `${ready} finished and waiting.`
            : `Nothing is finished yet. This week's set changes in ${untilLabel(state.now, state.weekEndsAt)}.`,
      },
      ...items,
      backRow(),
    ];
  }

  /** "Weekly · Find The Net", "Season 2 · Team Player", "Event · Air Show". */
  private challengeLabel(row: ChallengeRow): string {
    if (row.def.kind === 'season') return `Season ${row.stage ?? 1} · ${row.def.name}`;
    if (row.def.kind === 'event') return `Event · ${row.def.name}`;
    return `${row.def.premiumOnly ? 'Weekly+' : 'Weekly'} · ${row.def.name}`;
  }

  // -------------------------------------------------------------------------
  // Multiplayer screens
  // -------------------------------------------------------------------------

  /** A text row, reading the value it currently holds. */
  private textRow(field: TextFieldId, value: string, note?: string): MenuItem {
    const spec = TEXT_FIELDS[field];
    return {
      kind: 'text',
      id: `${TEXT_PREFIX}${field}`,
      label: spec.label,
      field,
      value,
      empty: value.length === 0,
      enabled: true,
      note: note ?? spec.hint,
    };
  }

  // -------------------------------------------------------------------------
  // Account screen
  // -------------------------------------------------------------------------

  /**
   * The landing screen. Three rows and no fourth.
   *
   * `Play Single Player` is deliberately not marked as the emphasised choice
   * even though it is the one that needs nothing: the account rows are what a
   * returning player wants and what a new one should at least see, and making
   * the skip the loudest button is how you end up with a game where nobody has
   * an account. It is still first-class, still one keypress, and still says
   * plainly that it costs nothing.
   */
  private welcomeItems(): MenuItem[] {
    const busy = this.account?.phase === 'busy';
    return [
      {
        kind: 'action',
        id: 'welcome-signin',
        label: 'Sign In',
        enabled: !busy,
        emphasis: !busy,
        lockLabel: 'Working',
        note: 'Your items, credits and progress are on the server, on whatever machine you play from.',
      },
      {
        kind: 'action',
        id: 'welcome-register',
        label: 'Create Account',
        enabled: !busy,
        lockLabel: 'Working',
        note: 'Takes an email and a password. Needed for multiplayer, the garage and the shop.',
      },
      {
        kind: 'action',
        id: 'welcome-guest',
        label: 'Play Single Player',
        enabled: true,
        note: 'Straight into a match against bots. No account, and nothing you do is saved.',
      },
    ];
  }

  /**
   * The account hub: who you are and how to stop being them, or — signed out —
   * the two doors, with the forms themselves one level down.
   *
   * Splitting the forms out is the whole point of the change. One screen that
   * held an email, a password, a display name, Sign In, Create Account, Resend
   * Verification and Forgot Password asked every arriving player to work out
   * which four of those seven rows were theirs. Now the screen you are on says
   * which one you are doing.
   */
  private accountItems(): MenuItem[] {
    const view = this.account;
    const busy = view?.phase === 'busy';
    const user = view?.user ?? null;

    if (view?.phase !== 'signedIn' || user === null) {
      return [
        {
          kind: 'action',
          id: 'welcome-signin',
          label: 'Sign In',
          enabled: !busy,
          emphasis: !busy,
          lockLabel: 'Working',
          note: busy ? (view?.message ?? 'Working…') : 'Email and password.',
        },
        {
          kind: 'action',
          id: 'welcome-register',
          label: 'Create Account',
          enabled: !busy,
          lockLabel: 'Working',
          note: busy ? (view?.message ?? 'Working…') : 'Unlocks multiplayer, the garage, the shop and the pass.',
        },
        backRow(),
      ];
    }

    if (view?.phase === 'signedIn' && user !== null) {
      return [
        {
          kind: 'value',
          id: 'account-user',
          label: 'Signed In As',
          valueLabel: user.displayName,
          enabled: false,
          lockLabel: '',
          note: 'The name every room sees. It belongs to the account, not this browser.',
        },
        {
          kind: 'value',
          id: 'account-email',
          label: 'Email',
          valueLabel: user.email,
          enabled: false,
          lockLabel: '',
        },
        {
          kind: 'action',
          id: 'account-logout',
          label: 'Sign Out',
          enabled: true,
          note: 'Multiplayer, the garage and the shop lock again until the next sign-in.',
        },
        backRow(),
      ];
    }

    // Signed out is handled above; this is unreachable and satisfies the
    // compiler's view of the branch.
    return [backRow()];
  }

  /** Email, password, sign in — and the one thing you need when it fails. */
  private signinItems(): MenuItem[] {
    const view = this.account;
    const busy = view?.phase === 'busy';
    const spec = TEXT_FIELDS.accountPassword;
    const haveCredentials = this.accountEmail.length > 0 && this.accountPassword.length > 0;

    return [
      this.textRow('accountEmail', this.accountEmail),
      // The password row renders DOTS. The buffer lives in memory only and is
      // dropped on sign-in; nothing account-shaped ever reaches localStorage
      // except the session token.
      this.textRow('accountPassword', displayTextValue(this.accountPassword, spec)),
      {
        kind: 'action',
        id: 'account-login',
        label: 'Sign In',
        enabled: !busy && haveCredentials,
        emphasis: !busy && haveCredentials,
        lockLabel: busy ? 'Working' : 'Fill In',
        note: busy
          ? (view?.message ?? 'Working…')
          : haveCredentials
            ? 'Signs in and loads your items, credits and progress from the server.'
            : 'Enter your email and password above first.',
      },
      {
        kind: 'action',
        id: 'account-forgot',
        label: 'Forgot Password',
        enabled: !busy && this.accountEmail.length > 0,
        lockLabel: busy ? 'Working' : 'Fill In',
        note: 'Emails a reset link to the address above. The link opens a page to pick a new password.',
      },
      {
        kind: 'action',
        id: 'welcome-register',
        label: 'No Account? Create One',
        enabled: !busy,
        lockLabel: 'Working',
        note: 'Keeps the email and password you have already typed.',
      },
      backRow(),
    ];
  }

  /**
   * Name, email, password, create.
   *
   * `Resend Verification Mail` lives here rather than on sign-in because it is
   * the second half of registering: you create the account, the mail does not
   * arrive, and this is the screen you are still looking at.
   */
  private registerItems(): MenuItem[] {
    const view = this.account;
    const busy = view?.phase === 'busy';
    const spec = TEXT_FIELDS.accountPassword;
    const haveCredentials = this.accountEmail.length > 0 && this.accountPassword.length > 0;
    const ready = haveCredentials && this.accountName.length > 0;

    return [
      this.textRow('accountName', this.accountName, 'The name every room will see.'),
      this.textRow('accountEmail', this.accountEmail),
      this.textRow('accountPassword', displayTextValue(this.accountPassword, spec)),
      {
        kind: 'action',
        id: 'account-register',
        label: 'Create Account',
        enabled: !busy && ready,
        emphasis: !busy && ready,
        lockLabel: busy ? 'Working' : 'Fill In',
        note: busy
          ? (view?.message ?? 'Working…')
          : ready
            ? 'Creates the account and emails a verification link. You sign in after clicking it.'
            : 'Fill in a name, an email and a password first.',
      },
      {
        kind: 'action',
        id: 'account-resend',
        label: 'Resend Verification Mail',
        enabled: !busy && haveCredentials,
        lockLabel: busy ? 'Working' : 'Fill In',
        note: 'Sends a fresh link to the email above, if that account exists and is unverified.',
      },
      {
        kind: 'action',
        id: 'welcome-signin',
        label: 'Already Registered? Sign In',
        enabled: !busy,
        lockLabel: 'Working',
        note: 'Keeps the email and password you have already typed.',
      },
      backRow(),
    ];
  }

  private multiplayerItems(): MenuItem[] {
    const net = this.settings.multiplayer;
    const stored = net.lastRoomCode;
    const modeLabel =
      MULTIPLAYER_MODE_CHOICES.find(c => c.value === net.mode)?.label ?? net.mode;

    return [
      {
        kind: 'action',
        id: 'net-quick',
        label: 'Quick Match',
        enabled: true,
        emphasis: true,
        note: `Queue for a match. Last picked: ${modeLabel}.`,
      },
      {
        kind: 'action',
        id: 'net-host',
        label: 'Host Private Match',
        enabled: true,
        note: 'Makes a room with a join code. It waits in a lobby until everyone readies up.',
      },
      this.textRow('roomCode', stored, 'Type a code to join a private match.'),
      {
        kind: 'action',
        id: 'net-rejoin',
        label: stored.length > 0 ? `Rejoin ${stored}` : 'Rejoin Last Room',
        // Disabled rather than hidden, so the row is there to be understood
        // before there is anything to rejoin.
        enabled: isRoomCode(stored),
        lockLabel: 'Empty',
        note: isRoomCode(stored)
          ? `Joins ${stored} again. Codes stop working when the room does.`
          : 'No code stored yet — join or host a private match first.',
      },
      this.textRow('serverAddress', net.serverAddress),
      {
        // A VALUE row, not a text row: since accounts arrived the name every
        // room sees is the account's, checked and stamped by the server. A
        // field here would be a field the server ignores.
        kind: 'value',
        id: 'net-name',
        label: 'Display Name',
        valueLabel: this.account?.user?.displayName ?? '—',
        enabled: false,
        lockLabel: '',
        note: 'Your account name. Everyone in the room sees it; the server enforces it.',
      },
      backRow(),
    ];
  }

  /**
   * The connecting screen. One row, and it is always usable.
   *
   * A connection attempt that cannot be abandoned is the single most common way
   * a menu strands somebody: a server that accepts a socket and then never
   * answers leaves this screen up forever, and there is no timeout short enough
   * to be right for both a LAN and a bad hotel connection.
   */
  private connectingItems(): MenuItem[] {
    return [
      {
        kind: 'action',
        id: 'net-cancel',
        label: 'Cancel',
        enabled: true,
        note: netStatusLine(this.net),
      },
    ];
  }

  private lobbyItems(): MenuItem[] {
    const net = this.net;
    const local = net.local;
    const ready = local?.ready === true;
    const ready$ = everyoneReady(net.players);

    return [
      {
        kind: 'toggle',
        id: 'net-ready',
        label: 'Ready',
        on: ready,
        enabled: local !== null,
        lockLabel: 'Waiting',
        note:
          local === null
            ? 'Waiting for the server to seat you.'
            : ready$
              ? 'Everyone is ready — the match starts on the next server tick.'
              : 'The match starts when everybody in the room is ready.',
      },
      {
        kind: 'action',
        id: 'net-team',
        label: 'Switch Team',
        // Three separate reasons it can be off, and each one gets its own
        // sentence rather than a shared "unavailable".
        enabled: local !== null && net.teams && !net.inProgress,
        lockLabel: net.inProgress ? 'Started' : 'Locked',
        note:
          local === null
            ? 'Waiting for the server to seat you.'
            : !net.teams
              ? 'Free play has no teams.'
              : net.inProgress
                ? 'The match has started; seats are fixed.'
                : 'Asks the server for the other side. It may refuse to keep the room balanced.',
      },
      {
        kind: 'action',
        id: 'net-copy',
        label: 'Copy Join Code',
        enabled: net.code !== null && net.canCopy,
        lockLabel: net.code === null ? 'No Code' : 'No Clipboard',
        note:
          net.code === null
            ? 'This room has no code — it came from the quick-match queue.'
            : net.canCopy
              ? `Puts ${net.code} on the clipboard.`
              : `This browser will not give the page a clipboard. The code is ${net.code}.`,
      },
      { kind: 'action', id: 'camera', label: 'Camera', enabled: true },
      { kind: 'action', id: 'audio', label: 'Audio', enabled: true },
      { kind: 'action', id: 'controls', label: 'Controls', enabled: true },
      { kind: 'action', id: 'net-leave', label: 'Leave Room', enabled: true },
    ];
  }

  /**
   * The disconnect screen: what happened, and at least two ways out.
   *
   * Reconnect is offered only when the server would actually still have the
   * seat — a version mismatch or a full server is not something trying again
   * fixes, and a button that re-runs a failure is worse than no button.
   */
  private disconnectedItems(): MenuItem[] {
    const net = this.net;
    return [
      {
        kind: 'action',
        id: 'net-reconnect',
        label: 'Reconnect',
        enabled: net.canReconnect,
        emphasis: net.canReconnect,
        lockLabel: 'Gone',
        note: net.canReconnect
          ? 'Claims the seat the server is still holding. It is held for thirty seconds.'
          : 'There is nothing to reconnect to.',
      },
      { kind: 'action', id: 'net-again', label: 'Back to Multiplayer', enabled: true },
      { kind: 'action', id: 'quit', label: 'Main Menu', enabled: true },
    ];
  }

  /** Which of an action's two key slots left/right has armed. */
  private bindSlot = 0;

  // -------------------------------------------------------------------------
  // Write side
  // -------------------------------------------------------------------------

  dispatch(action: NavAction): MenuEffect[] {
    // The bind prompt swallows navigation: while it is up, every key is a
    // candidate binding and goes through `captureBindKey` instead.
    if (this.screen === 'bind') {
      if (action === 'back') this.cancelBind();
      return [];
    }
    // The text prompt swallows it for the same reason and one more: `Space` and
    // `Backspace` are both `NavAction`s (see ./nav-input) and both are ordinary
    // characters in an address. Every key while this is open goes through
    // `captureTextKey`.
    if (this.screen === 'text') {
      if (action === 'back') this.closeText('');
      return [];
    }

    switch (action) {
      case 'up':
        this.moveFocus(-1);
        return [];
      case 'down':
        this.moveFocus(1);
        return [];
      case 'left':
        return this.adjust(-1);
      case 'right':
        return this.adjust(1);
      case 'confirm':
        return this.confirm();
      case 'back':
        return this.back();
    }
  }

  private moveFocus(delta: number): void {
    const count = this.items.length;
    if (count === 0) return;
    this.toast = '';
    this.focusByScreen.set(this.screen, (this.focusIndex + delta + count) % count);
  }

  private adjust(direction: 1 | -1): MenuEffect[] {
    const item = this.focusedItem;
    if (!item) return [];
    if (!item.enabled) {
      this.toast = item.note ?? 'Not available yet.';
      return [];
    }
    this.toast = '';

    switch (item.kind) {
      case 'choice':
        return this.adjustChoice(item.id, direction);
      case 'toggle':
        return this.setToggle(item.id, !item.on);
      case 'slider':
        return this.adjustSlider(item.id, direction);
      case 'bind':
        // Left/right pick which of the two key slots Enter will rebind.
        this.bindSlot = (this.bindSlot + direction + 2) % 2;
        return [];
      case 'text':
      case 'action':
      case 'value':
        // None has a "next value" — an address has no adjacent address, and a
        // price is not a setting.
        return [];
    }
  }

  private adjustChoice(id: string, direction: 1 | -1): MenuEffect[] {
    const setup = this.settings.setup;
    switch (id) {
      case 'mode':
        setup.mode = cycleChoice(MODE_CHOICES, setup.mode, direction, true);
        return ['settings-changed'];
      case 'difficulty':
        // `implementedOnly` is what makes an unbacked `learned` unreachable: it
        // is still in the table (and still named in the row's note) but the
        // cycler steps over it, so no keypress sequence can select a tier with
        // no policy. A checkpoint flips the flag in the table itself, not the
        // rule here — the rule is the same either way.
        setup.difficulty = cycleChoice(
          this.difficultyChoices,
          setup.difficulty,
          direction,
          true
        );
        return ['settings-changed'];
      case 'team':
        setup.team = cycleChoice(TEAM_CHOICES, setup.team, direction, true);
        return ['settings-changed'];
      case 'car':
        setup.car = cycleChoice(CAR_CHOICES, setup.car, direction, true);
        return ['settings-changed'];
      case 'length':
        setup.length = cycleChoice(LENGTH_CHOICES, setup.length, direction, true);
        return ['settings-changed'];
      // The garage's team toggle is a VIEW state, not a setting: it says which
      // of the two loadouts every row below it is about, and it deliberately
      // does not move `setup.team`.
      case 'garage-team':
        this.garageTeam = this.garageTeam === 0 ? 1 : 0;
        return ['loadout-changed'];

      case 'garage-primary':
      case 'garage-accent': {
        const state = this.progression;
        if (state === null) return [];
        const accent = id === 'garage-accent';
        const loadout = activeLoadout(state.profile, this.garageTeam);
        const next = cycleColor(
          colorChoices(this.garageTeam, accent),
          accent ? loadout.accentColor : loadout.primaryColor,
          direction
        );
        this.garageEdit = {
          kind: 'colors',
          team: this.garageTeam,
          primary: accent ? loadout.primaryColor : next,
          accent: accent ? next : loadout.accentColor,
        };
        return ['loadout-changed'];
      }

      case 'pass-tier': {
        // Wraps, like every other cycler here, so a player at tier 1 pressing
        // left lands on the Pro Tiers rather than on nothing at all.
        const state = this.progression;
        if (state === null) return [];
        const ceiling = this.tierCeiling(state);
        this.passTier = ((this.passTier - 1 + direction + ceiling) % ceiling) + 1;
        return [];
      }
      default:
        if (id.startsWith(MUTATOR_PREFIX)) {
          return this.adjustMutator(id.slice(MUTATOR_PREFIX.length), direction);
        }
        if (id.startsWith(TRAINING_PREFIX)) {
          return this.adjustTraining(id.slice(TRAINING_PREFIX.length), direction);
        }
        if (id.startsWith(TOUCH_PREFIX)) {
          return this.adjustTouch(id.slice(TOUCH_PREFIX.length), direction);
        }
        if (id === 'ranked-mode') {
          const next = cycleChoice(MULTIPLAYER_MODE_CHOICES, this.rankedMode, direction, true);
          if (next === this.rankedMode) return [];
          this.rankedMode = next;
          // The board is per mode and is not held for every one of them; the
          // row moving is the request.
          return ['ranked-refresh'];
        }
        return [];
    }
  }

  /**
   * Training rows, data-driven off `TRAINING_ROWS` for the same reason the
   * mutator rows are: a row added to that table needs no case here. Every
   * training choice is implemented, so `implementedOnly` is moot — it is passed
   * true anyway, because the day one of them is not, the rule should already be
   * in place rather than being remembered.
   */
  private adjustTraining(key: string, direction: 1 | -1): MenuEffect[] {
    const row = TRAINING_ROWS.find(r => r.key === key);
    if (!row) return [];

    const training = this.settings.training as unknown as Record<string, string | number>;
    const next = cycleChoice(row.choices, training[key], direction, true);
    if (next === training[key]) return [];

    training[key] = next;
    return ['settings-changed', 'training-config-changed'];
  }

  /**
   * The two on-screen-control rows.
   *
   * The throttle row is guarded here as well as being locked in
   * `controlItems`. A locked row already refuses to move — but the guard is
   * the half that does not depend on the renderer having drawn the lock, and
   * "unreachable by any keypress sequence" is the rule this file applies to
   * every option the game cannot honour.
   */
  private adjustTouch(key: string, direction: 1 | -1): MenuEffect[] {
    const touch = this.settings.touch;
    if (key === 'mode') {
      const next = cycleChoice(TOUCH_MODE_CHOICES, touch.mode, direction, true);
      if (next === touch.mode) return [];
      touch.mode = next;
      return ['settings-changed'];
    }
    if (key === 'autoThrottle') {
      if (touch.mode === 'off') return [];
      const next = cycleChoice(AUTO_THROTTLE_CHOICES, touch.autoThrottle, direction, true);
      if (next === touch.autoThrottle) return [];
      touch.autoThrottle = next;
      return ['settings-changed'];
    }
    return [];
  }

  /**
   * Mutator rows are data-driven off `MUTATOR_ROWS`, so a row added to that
   * table needs no case here. `implementedOnly` is true for the same reason it
   * is on the difficulty row: an option the simulation cannot honour must be
   * unreachable by any keypress sequence, not merely discouraged.
   */
  private adjustMutator(key: string, direction: 1 | -1): MenuEffect[] {
    const row = MUTATOR_ROWS.find(r => r.key === key);
    if (!row) return [];

    const mutators = this.settings.setup.mutators as unknown as Record<string, string>;
    const next = cycleChoice(row.choices, mutators[key], direction, true);
    if (next === mutators[key]) return [];

    mutators[key] = next;
    return ['settings-changed'];
  }

  private setToggle(id: string, value: boolean): MenuEffect[] {
    if (id === 'overtime') {
      this.settings.setup.overtime = value;
      return ['settings-changed'];
    }
    if (id === 'ballcam') {
      this.settings.ballCam = value;
      return ['settings-changed'];
    }
    if (id === 'audio:muted') {
      this.settings.audio.muted = value;
      return ['settings-changed'];
    }
    if (id === 'train-watch') {
      this.settings.training.watchLive = value;
      return ['settings-changed', 'training-watch-changed'];
    }
    if (id === 'net-ready') {
      // Nothing local moves. `value` is the request; the row shows what the
      // server last said, and it changes when the next `RoomState` arrives.
      return ['net-ready'];
    }
    return [];
  }

  private adjustSlider(id: string, direction: 1 | -1): MenuEffect[] {
    if (id === 'audio:volume') {
      // 5% a press: fine enough to find a level, coarse enough that reaching
      // silence from full does not take twenty presses.
      const stepped = stepSlider(this.settings.audio.volume, 0.05, direction);
      this.settings.audio.volume = Math.max(0, Math.min(1, stepped));
      return ['settings-changed'];
    }
    const key = id.slice('cam:'.length) as keyof CameraSettings;
    const slider = CAMERA_SLIDERS.find(s => s.key === key);
    if (!slider) return [];
    const stepped = stepSlider(this.settings.camera[key], slider.step, direction);
    // Clamping goes through the camera module so the menu can never invent a
    // value the camera would reject — the ranges live there, not here.
    this.settings.camera = clampCameraSettings({ [key]: stepped }, this.settings.camera);
    return ['settings-changed'];
  }

  private confirm(): MenuEffect[] {
    const item = this.focusedItem;
    if (!item) return [];
    if (!item.enabled) {
      this.toast = item.note ?? 'Not available yet.';
      return [];
    }
    this.toast = '';

    if (item.kind === 'toggle') return this.setToggle(item.id, !item.on);
    if (item.kind === 'choice') return this.adjust(1);
    if (item.kind === 'bind') {
      this.bindTarget = { action: item.action, slot: this.bindSlot };
      this.push('bind');
      return [];
    }
    if (item.kind === 'text') {
      // From the source, not from the row: a masked row's `value` is dots, and
      // a prompt opened on dots would edit the mask rather than the password.
      this.openText(item.field, this.textSource(item.field));
      return [];
    }
    return this.runAction(item.id);
  }

  /** The value a text field currently holds, wherever it lives. */
  private textSource(field: TextFieldId): string {
    switch (field) {
      case 'serverAddress':
        return this.settings.multiplayer.serverAddress;
      case 'roomCode':
        return this.settings.multiplayer.lastRoomCode;
      case 'displayName':
        return this.settings.multiplayer.displayName;
      case 'accountEmail':
        return this.accountEmail;
      case 'accountPassword':
        return this.accountPassword;
      case 'accountName':
        return this.accountName;
    }
  }

  private runAction(id: string): MenuEffect[] {
    if (id.startsWith(SHOP_PREFIX)) {
      const state = this.progression;
      if (state === null) return [];
      const [row, index] = id.slice(SHOP_PREFIX.length).split(':');
      const found = shopRows(state).find(r => r.row === row && String(r.index) === index);
      // A rotation that turned over between the render and the press: the row
      // the player pressed is not the row that would be bought, so nothing is.
      if (found === undefined || found.problem !== null) return [];
      this.shopSelection = found;
      return ['shop-buy'];
    }

    // --- garage ---------------------------------------------------------

    if (id.startsWith(GARAGE_SLOT_PREFIX)) {
      const slot = id.slice(GARAGE_SLOT_PREFIX.length) as CosmeticSlot;
      if (!GARAGE_SLOTS.includes(slot)) return [];
      this.garageSlot = slot;
      this.push('garageSlot');
      return [];
    }

    if (id.startsWith(GARAGE_ITEM_PREFIX)) {
      const state = this.progression;
      const slot = this.garageSlot;
      if (state === null || slot === null) return [];
      const rest = id.slice(GARAGE_ITEM_PREFIX.length);
      // A locked row is focusable so its note can be read, and `confirm`
      // already refuses a disabled row — this is the belt to that brace.
      if (rest.startsWith('locked:')) return [];
      const instanceId = rest === 'none' ? null : rest;
      if (instanceId !== null && !ownsInstance(state.profile, instanceId)) return [];
      this.garageEdit = { kind: 'equip', team: this.garageTeam, slot, instanceId };
      // Stay on the picker. Equipping is something a player does several times
      // in a row while looking at the preview, and popping after each one would
      // make trying three wheels into six keypresses.
      this.toast =
        instanceId === null
          ? `${SLOT_LABEL[slot]} cleared.`
          : `${itemForInstance(state.profile, instanceId)?.name ?? 'Item'} equipped.`;
      return ['loadout-changed'];
    }

    if (id.startsWith(GARAGE_PRESET_PREFIX)) {
      const state = this.progression;
      if (state === null) return [];
      const wanted = id.slice(GARAGE_PRESET_PREFIX.length);
      if (!state.profile.presets.some(p => p.id === wanted)) return [];
      this.garageEdit = { kind: 'preset', id: wanted };
      return ['loadout-changed'];
    }

    if (id.startsWith(CHALLENGE_PREFIX)) {
      const state = this.progression;
      if (state === null) return [];
      const wanted = id.slice(CHALLENGE_PREFIX.length);
      const row = challengeRows(state).find(r => r.def.id === wanted);
      if (row === undefined || !row.complete || row.claimed || row.expired) return [];
      this.challengeSelection = wanted;
      return ['challenge-claim'];
    }

    if (id.startsWith(CHECKPOINT_PREFIX)) {
      const wanted = id.slice(CHECKPOINT_PREFIX.length);
      if (!this.checkpoints.some(r => r.id === wanted)) return [];
      this.selectedCheckpointId = wanted;
      this.push('checkpoint');
      return [];
    }

    if (id.startsWith(DRILL_PREFIX)) {
      const wanted = id.slice(DRILL_PREFIX.length);
      if (drillById(wanted) === undefined) return [];
      this.selectedDrillId = wanted;
      // `reset`, not `push`: a run is a session, and leaving it must land on
      // the main menu the way leaving a match does — not back on the list with
      // a world still running behind it.
      this.reset('countdown');
      return ['start-drill'];
    }

    if (id.startsWith('mode:')) {
      const value = id.slice('mode:'.length);
      const choice = MODE_CHOICES.find(c => c.value === value);
      if (!choice || !choice.implemented) return [];
      this.settings.setup.mode = choice.value;
      this.push('setup');
      return ['settings-changed'];
    }

    if (id.startsWith(NET_MODE_PREFIX)) {
      const value = id.slice(NET_MODE_PREFIX.length);
      const choice = MULTIPLAYER_MODE_CHOICES.find(c => c.value === value);
      if (!choice || !choice.implemented) return [];
      this.settings.multiplayer.mode = choice.value;
      // No code and no create: the server reads that as "put me in the queue
      // for this mode", and joins me into a half-empty room of it if there is
      // one. See `onJoin` in server/server.ts.
      return ['settings-changed', ...this.connect({ mode: choice.value, code: null, create: false })];
    }

    switch (id) {
      case 'play':
        this.push('mode');
        return [];

      case 'account':
        this.push('account');
        return [];

      case 'welcome-signin':
        // From `register` this is a SWAP, not a push: the two forms are
        // alternatives, and stacking them would make Escape walk back through
        // a screen the player explicitly turned down.
        this.swapOrPush('signin', 'register');
        return [];

      case 'welcome-register':
        this.swapOrPush('register', 'signin');
        return [];

      case 'welcome-guest':
        // Everything that needs a session stays locked and says so; this is
        // only a decision to stop being asked about it right now.
        this.reset('main');
        return [];

      case 'account-login':
        return ['account-login'];
      case 'account-register':
        return ['account-register'];
      case 'account-resend':
        return ['account-resend'];
      case 'account-forgot':
        return ['account-forgot'];
      case 'account-logout':
        // Back to the front door. Staying on `main` would leave a player
        // looking at six rows that just locked themselves, with the thing that
        // unlocks them one screen away and no sign of it.
        this.reset('welcome');
        return ['account-logout'];

      case 'multiplayer':
        this.push('multiplayer');
        return [];

      case 'net-quick':
        this.push('netmode');
        return [];

      case 'net-host':
        return this.connect({ mode: 'private', code: null, create: true });

      case 'net-rejoin': {
        const code = this.settings.multiplayer.lastRoomCode;
        if (!isRoomCode(code)) return [];
        return this.connect({ mode: 'private', code, create: false });
      }

      case 'net-ready':
        return ['net-ready'];

      case 'net-team':
        return ['net-team'];

      case 'net-copy':
        this.toast = `Join code ${this.net.code ?? ''} copied.`;
        return ['net-copy-code'];

      case 'net-cancel':
        // Back to the screen the attempt started from, with the socket dropped.
        this.stack = ['main', 'multiplayer'];
        return ['net-cancel'];

      case 'net-leave':
        this.netMatch = false;
        this.stack = ['main', 'multiplayer'];
        return ['net-leave'];

      case 'net-again':
        this.netMatch = false;
        this.stack = ['main', 'multiplayer'];
        // The old session is finished either way; saying so is what stops a
        // dead socket being held open behind the menu.
        return ['net-cancel'];

      case 'net-reconnect':
        if (!this.net.canReconnect) return [];
        this.reset('connecting');
        this.stack = ['main', 'multiplayer', 'connecting'];
        return ['net-reconnect'];
      case 'mutators':
        this.push('mutators');
        return [];
      case 'camera':
        this.push('camera');
        return [];
      case 'audio':
        this.push('audio');
        return [];
      case 'controls':
        this.push('controls');
        return [];

      case 'garage':
        this.garageSlot = null;
        this.push('garage');
        return [];

      case 'garage-presets':
        this.push('garagePresets');
        return [];

      case 'garage-preset-add':
        this.garageEdit = { kind: 'add-preset' };
        this.toast = 'New preset created and made active.';
        return ['loadout-changed'];

      // Each of the three re-reads the calendar on arrival, for the same reason
      // the training screen re-reads the checkpoint store: a tab left open
      // across a Wednesday would otherwise still be showing last week's set,
      // and a tab left open across midnight yesterday's shop.
      case 'shop':
        this.push('shop');
        return ['progression-refresh'];

      case 'rocketpass':
        this.push('rocketpass');
        return ['progression-refresh'];

      case 'challenges':
        this.push('challenges');
        return ['progression-refresh'];

      case 'pass-free':
        return ['pass-claim-free'];
      case 'pass-premium-claim':
        return ['pass-claim-premium'];
      case 'pass-claim-all':
        return ['pass-claim-all'];
      case 'pass-unlock':
        return ['pass-unlock'];
      case 'chal-claim-all':
        return ['challenge-claim-all'];

      case 'ranked':
        // Opened on the mode they last queued for — see the note on
        // `rankedMode`. A guest has never queued, so that is `duel`, which is
        // also the busiest ladder and the right one to open on.
        this.rankedMode = this.settings.multiplayer.mode;
        this.push('ranked');
        return ['ranked-refresh'];

      case 'drills':
      case 'drill-list':
        // From the result screen this REPLACES it rather than stacking, so
        // Escape from the list lands on the main menu instead of on the tally
        // of a run that is over.
        if (this.screen === 'drillresult') this.reset('drills');
        else this.push('drills');
        return [];

      case 'drill-again': {
        if (this.selectedDrillId === null) return [];
        this.reset('countdown');
        return ['start-drill'];
      }

      case 'training':
        this.push('training');
        // The store is asynchronous and may have been written by another tab,
        // so the list is re-read on arrival rather than trusted from last time.
        return ['checkpoints-refresh'];

      case 'checkpoints':
        this.push('checkpoints');
        return ['checkpoints-refresh'];

      // One row, three meanings, because "the button that starts it" and "the
      // button that stops it" being different rows is how you pause a run you
      // meant to resume.
      case 'train-run':
        if (isTrainingActive(this.trainingPhase)) return ['training-pause'];
        if (this.trainingPhase === 'paused') return ['training-resume'];
        return ['training-start'];

      case 'train-stop':
        return ['training-stop'];

      case 'ckpt-save':
        return ['checkpoint-save'];
      case 'ckpt-import':
        return ['checkpoint-import'];
      case 'ckpt-load':
        return ['checkpoint-load'];
      case 'ckpt-export':
        return ['checkpoint-export'];
      case 'ckpt-delete':
        return ['checkpoint-delete'];

      case 'start':
        this.reset('countdown');
        return ['start-match'];

      case 'reset-camera':
        this.settings.camera = defaultCameraSettings();
        this.settings.ballCam = DEFAULT_BALL_CAM;
        this.toast = 'Camera restored to defaults.';
        return ['settings-changed'];

      case 'reset-audio':
        this.settings.audio = defaultAudioSettings();
        this.toast = 'Audio restored to defaults.';
        return ['settings-changed'];

      case 'reset-bindings':
        this.settings.bindings = resetBindings();
        this.toast = 'Controls restored to defaults.';
        return ['settings-changed'];

      case 'resume':
      case 'back':
        return this.back();

      case 'restart':
      case 'rematch':
        this.reset('countdown');
        return ['restart-match'];

      case 'quit': {
        // One row, two teardowns. A networked match has a world AND a socket,
        // and leaving without the second holds a seat nobody is sitting in for
        // the whole reconnect grace.
        const networked = this.netMatch || NET_SCREENS.includes(this.screen);
        this.netMatch = false;
        this.reset('main');
        return networked ? ['quit-match', 'net-leave'] : ['quit-match'];
      }

      case 'change-setup':
        this.stack = ['main', 'mode', 'setup'];
        return ['quit-match'];

      default:
        return [];
    }
  }

  private back(): MenuEffect[] {
    // During play there is nothing above to pop to, so Escape opens the pause
    // menu instead. Documented exception; see the header.
    if (this.screen === 'match' || this.screen === 'countdown') {
      this.push('pause');
      return [];
    }
    if (this.screen === 'netmatch') {
      this.push('netpause');
      return [];
    }
    // The scoreboard's only sensible "back" is out of the finished match.
    if (this.screen === 'postmatch') {
      return this.runAction('quit');
    }
    // Escape out of any of these is a decision about a live socket, not just a
    // pop: a lobby left behind the menu keeps a seat, and a connection attempt
    // left running behind it reconnects into a screen the player has left.
    if (this.screen === 'connecting') return this.runAction('net-cancel');
    if (this.screen === 'lobby') return this.runAction('net-leave');
    // `disconnected` is a reset stack with nothing under it, so popping would
    // leave the player exactly where they were. It is the shape a dead end
    // takes, and this is the line that stops it being one.
    if (this.screen === 'disconnected') return this.runAction('net-again');

    // Escape on the main menu has never meant anything — there is nothing
    // above it to pop. For a guest there now is: the screen they skipped to
    // get here. Signed in it stays a no-op, because `welcome` has nothing to
    // offer someone who has already answered all three of its questions.
    if (this.screen === 'main' && this.stack.length === 1 && !this.signedIn) {
      this.reset('welcome');
      return [];
    }

    this.toast = '';
    if (this.stack.length > 1) this.stack.pop();
    return [];
  }

  // -------------------------------------------------------------------------
  // Multiplayer transitions
  // -------------------------------------------------------------------------

  /** Record what to connect to and show the connecting screen. */
  private connect(intent: MenuNetIntent): MenuEffect[] {
    this.netIntent = intent;
    this.netMatch = false;
    // An explicit stack rather than a push: this is reachable from the
    // multiplayer screen, from the mode picker and from the disconnect screen,
    // and Escape out of it must land on the same place from all three.
    this.stack = ['main', 'multiplayer', 'connecting'];
    this.focusByScreen.set('connecting', 0);
    this.toast = '';
    this.textTarget = null;
    this.textBuffer = '';
    return ['net-connect'];
  }

  /**
   * A discrete moment in a session's life. The runtime raises these; a mere
   * state update goes through `setNetView` and never navigates.
   *
   * Every branch is guarded on the player still being somewhere multiplayer.
   * A room closing while they are reading the controls screen must change what
   * the multiplayer screen says and must not move them, which is the same rule
   * `setTrainingPhase` follows and for the same reason.
   */
  notifyNet(event: NetUiEvent): void {
    if (!this.inNetContext) return;
    this.toast = '';
    // A prompt open over the multiplayer screen belongs to the screen it was
    // opened from. Every branch below replaces the stack, so leaving the target
    // set would strand a modal on a screen with no field to edit.
    this.textTarget = null;
    this.textBuffer = '';

    switch (event) {
      case 'queued':
        if (this.screen !== 'connecting') this.stack = ['main', 'multiplayer', 'connecting'];
        return;
      case 'lobby':
        this.netMatch = false;
        this.stack = ['main', 'multiplayer', 'lobby'];
        this.focusByScreen.set('lobby', 0);
        return;
      case 'started':
        this.netMatch = true;
        this.reset('netmatch');
        return;
      case 'closed':
        // A clean leave has already navigated (see `net-leave`); this is the
        // case nobody asked for, and it gets a screen that says what happened.
        if (this.net.failure === null) return;
        this.reset('disconnected');
        return;
    }
  }

  /** True while the player is anywhere a session's fate is relevant. */
  private get inNetContext(): boolean {
    return this.stack.some(s => NET_SCREENS.includes(s));
  }

  // -------------------------------------------------------------------------
  // Bind capture
  // -------------------------------------------------------------------------

  /**
   * Handle a raw key press while the bind prompt is up. Escape cancels,
   * Backspace/Delete clears the slot, anything else binds. Returns the effects
   * to apply; a no-op press (prompt not open) returns nothing.
   */
  captureBindKey(code: string): MenuEffect[] {
    if (this.screen !== 'bind' || !this.bindTarget) return [];
    const target = this.bindTarget;

    if (code === 'Escape') {
      this.cancelBind();
      return [];
    }

    if (code === 'Backspace' || code === 'Delete') {
      this.settings.bindings = clearBinding(this.settings.bindings, target.action, target.slot);
      this.closeBind('Cleared.');
      return ['settings-changed'];
    }

    const result = bindKey(this.settings.bindings, target.action, target.slot, code);
    if (!result.applied) {
      this.toast = 'That key is reserved by the menu.';
      return [];
    }
    this.settings.bindings = result.bindings;
    this.closeBind(
      result.displaced.length > 0
        ? `Unbound from ${result.displaced.length} conflicting action${
            result.displaced.length === 1 ? '' : 's'
          }.`
        : ''
    );
    return ['settings-changed'];
  }

  // -------------------------------------------------------------------------
  // Text capture
  // -------------------------------------------------------------------------

  /** The field the prompt is editing, for the view's title and hint. */
  get textField(): TextFieldId | null {
    return this.textTarget?.field ?? null;
  }

  private openText(field: TextFieldId, value: string): void {
    this.textTarget = {
      field,
      // Typing a join code IS the join. Committing it and then having to find a
      // second row to press would be two steps for one decision.
      connectOnCommit: field === 'roomCode',
    };
    this.textBuffer = value;
    this.push('text');
  }

  /**
   * One key press while the text prompt is up.
   *
   * Mirrors `captureBindKey` exactly, including the contract: it returns the
   * effects to apply, and a press that changed nothing returns nothing. A
   * commit that fails validation leaves the prompt OPEN with a toast, because
   * closing it would throw away what was typed at the moment the player most
   * wants to fix one character of it.
   */
  captureTextKey(event: {
    code: string;
    key: string;
    ctrlKey?: boolean;
    metaKey?: boolean;
    altKey?: boolean;
  }): MenuEffect[] {
    const target = this.textTarget;
    if (this.screen !== 'text' || target === null) return [];

    const result = editText(this.textBuffer, event, TEXT_FIELDS[target.field]);
    this.textBuffer = result.buffer;

    switch (result.outcome) {
      case 'edit':
        this.toast = '';
        return [];
      case 'ignored':
        return [];
      case 'cancel':
        this.closeText('');
        return [];
      case 'commit':
        return this.commitTextBuffer();
    }
  }

  /** Text arriving through a paste, which is how an address usually gets in. */
  captureTextPaste(text: string): MenuEffect[] {
    const target = this.textTarget;
    if (this.screen !== 'text' || target === null) return [];
    this.textBuffer = pasteText(this.textBuffer, text, TEXT_FIELDS[target.field]);
    this.toast = '';
    return [];
  }

  private commitTextBuffer(): MenuEffect[] {
    const target = this.textTarget;
    if (target === null) return [];

    const commit = commitText(target.field, this.textBuffer);
    if (commit.value === null) {
      this.toast = commit.error;
      return [];
    }

    // The account fields live on the model, not in settings: nothing typed
    // here may ride `saveSettings` into localStorage — least of all a password.
    if (
      target.field === 'accountEmail' ||
      target.field === 'accountPassword' ||
      target.field === 'accountName'
    ) {
      if (target.field === 'accountEmail') this.accountEmail = commit.value;
      else if (target.field === 'accountPassword') this.accountPassword = commit.value;
      else this.accountName = commit.value;
      this.closeText('');
      return [];
    }

    const net = this.settings.multiplayer;
    switch (target.field) {
      case 'serverAddress':
        net.serverAddress = commit.value;
        break;
      case 'displayName':
        net.displayName = commit.value;
        break;
      case 'roomCode':
        net.lastRoomCode = commit.value;
        break;
    }

    const code = commit.value;
    const connect = target.connectOnCommit;
    this.closeText('');
    if (!connect) return ['settings-changed'];
    return ['settings-changed', ...this.connect({ mode: 'private', code, create: false })];
  }

  private closeText(message: string): void {
    this.textTarget = null;
    this.textBuffer = '';
    if (this.screen === 'text') this.stack.pop();
    this.toast = message;
  }

  private cancelBind(): void {
    this.closeBind('');
  }

  private closeBind(message: string): void {
    this.bindTarget = null;
    if (this.screen === 'bind') this.stack.pop();
    this.toast = message;
  }

  // -------------------------------------------------------------------------
  // World events
  // -------------------------------------------------------------------------

  notify(event: WorldEvent, result: MatchResult | null = null): void {
    if (event === 'countdown-complete') {
      // Replace in place rather than resetting: the pause menu may be open on
      // top of the countdown, and closing it must land on `match`, not reopen
      // a countdown that has already finished.
      this.stack = this.stack.map(s => (s === 'countdown' ? 'match' : s));
      return;
    }
    // A finished drill carries its tally on `drillSummary`, written by the
    // view before it notifies — a `MatchResult` has no field that could hold
    // "six of ten" without lying about what a score is.
    if (event === 'drill-finished') {
      this.reset('drillresult');
      return;
    }
    this.result = result;
    this.reset('postmatch');
  }

  // -------------------------------------------------------------------------
  // Stack primitives
  // -------------------------------------------------------------------------

  private push(screen: Screen): void {
    this.toast = '';
    this.stack.push(screen);
    if (!this.focusByScreen.has(screen)) this.focusByScreen.set(screen, 0);
  }

  /**
   * Push `screen`, or replace the top of the stack when it is `insteadOf`.
   *
   * For pairs of screens that are alternatives rather than steps — sign in and
   * register — so that "actually, I do not have an account" leaves one form
   * open rather than two stacked, and Escape from either lands on whatever
   * opened the first one.
   */
  private swapOrPush(screen: Screen, insteadOf: Screen): void {
    if (this.screen === insteadOf) this.stack.pop();
    this.push(screen);
  }

  private reset(screen: Screen): void {
    this.toast = '';
    this.bindTarget = null;
    this.textTarget = null;
    this.textBuffer = '';
    this.stack = [screen];
    this.focusByScreen.set(screen, 0);
  }
}

/** Title and subtitle for each screen, for the view. */
export const SCREEN_TITLES: Record<Screen, { title: string; sub: string }> = {
  welcome: { title: 'Vroomball', sub: 'Aerial car soccer' },
  main: { title: 'Vroomball', sub: 'Aerial car soccer' },
  mode: { title: 'Playlist', sub: 'Choose a mode' },
  setup: { title: 'Match Setup', sub: 'Configure and start' },
  mutators: { title: 'Mutators', sub: 'Change the rules of the match' },
  camera: { title: 'Camera', sub: 'Applies immediately' },
  audio: { title: 'Audio', sub: 'Applies immediately' },
  controls: { title: 'Controls', sub: 'Enter rebinds the armed slot' },
  bind: { title: 'Press A Key', sub: 'Esc cancels · Backspace clears' },
  text: { title: 'Type A Value', sub: 'Enter accepts · Esc cancels' },
  account: { title: 'Account', sub: 'Your progress lives on the server' },
  signin: { title: 'Sign In', sub: 'Welcome back' },
  register: { title: 'Create Account', sub: 'Takes an email and a password' },
  multiplayer: { title: 'Multiplayer', sub: 'Play against other people' },
  netmode: { title: 'Quick Match', sub: 'Pick a format and queue' },
  connecting: { title: 'Connecting', sub: '' },
  lobby: { title: 'Lobby', sub: 'Ready up to start' },
  netmatch: { title: '', sub: '' },
  netpause: { title: 'Menu', sub: 'The match is still running' },
  disconnected: { title: 'Disconnected', sub: '' },
  ranked: { title: 'Ranked', sub: 'Your standing and the ladder' },
  drills: { title: 'Practice', sub: 'Ten attempts · no opponent' },
  drillresult: { title: 'Drill Over', sub: '' },
  training: { title: 'Training', sub: 'PPO · reinforcement learning in this tab' },
  checkpoints: { title: 'Checkpoints', sub: 'Save, load, import, export' },
  checkpoint: { title: 'Checkpoint', sub: '' },
  garage: { title: 'Garage', sub: 'Two loadouts, one per team colour' },
  garageSlot: { title: 'Garage', sub: 'Owned first, then the rest of the catalogue' },
  garagePresets: { title: 'Presets', sub: 'A preset is a pair of loadouts' },
  shop: { title: 'Item Shop', sub: 'Rotates daily · Credits are earned' },
  rocketpass: { title: 'Rocket Pass', sub: '70 tiers, then Pro Tiers' },
  challenges: { title: 'Challenges', sub: 'Weekly · Season · Event' },
  countdown: { title: 'Get Ready', sub: '' },
  match: { title: '', sub: '' },
  pause: { title: 'Paused', sub: '' },
  postmatch: { title: 'Full Time', sub: '' },
};

